"use client"

import type React from "react"
import { useState, useCallback, useRef, useEffect } from "react"
import { useGameState } from "@/hooks/use-game-state"
import { useGameLoop } from "@/hooks/use-game-loop"
import { usePageVisibility } from "@/hooks/use-page-visibility"
import { useCrystals } from "@/hooks/use-crystals"
import { recordRun } from "@/lib/player-stats"
import { discover, discoverMany } from "@/lib/codex-discovery"
import { getPlayerPrefs, usePlayerPrefs } from "@/lib/player-prefs"
import { useAuth } from "@/hooks/use-auth"
import { signedFetch } from "@/lib/api-auth-client"
import { useViewport } from "@/contexts/viewport-context"
import { gameNow, setGameClockRate } from "@/utils/game-clock"
import { PillarboxLayout } from "@/components/layout/pillarbox-layout"
import { GameCanvas } from "./game-canvas"
import { GameHUD } from "./game-hud"
import { GameBottomBar } from "./game-bottom-bar"
import { TitleScreen } from "./screens/title-screen"
import { GameOverScreen } from "./screens/game-over-screen"
import { PauseMenu } from "./game-ui/menus/pause-menu"
import { DropPodMinigame } from "./drop-pod-minigame"
import { LandingSequence } from "./screens/landing-sequence"
import { HeroSelectScreen } from "./screens/hero-select-screen"
import { RadialTowerMenu } from "./game-ui/menus/radial-tower-menu"
import { RadialDroppodMenu } from "./game-ui/menus/radial-droppod-menu"
import { RadialShrineMenu } from "./game-ui/menus/radial-shrine-menu"
import { PhaseTerminalWindow } from "./phase-terminal-window"
import { DroppedItemMenu } from "./game-ui/menus/dropped-item-menu"
import { TowerInfoPanel } from "./tower-info-panel"
import { NextWaveButton } from "./next-wave-button"
import { LevelCompleteScreen } from "./screens/level-complete-screen"
import { SkillUpgradeButton } from "./shop/skill-upgrade-button"
import type { TowerType } from "@/types/tower.types"
import type { Position, ViewportState, AbductionPhase, AbductionAnimationState } from "@/types/game.types"
import { pointInCircle, isTileOnPath } from "@/utils/collision"
import { canPlaceTower } from "@/systems/tower-system"
import { towerTypes } from "@/data/tower-types"
import {
  loadGameStateAfterAuth,
  clearSavedGameState,
  savePendingRun,
  loadPendingRun,
  clearPendingRun,
  type PersistedGameState,
} from "@/services/game-state-persistence"
import { RedoIcon } from "@/components/icons/pixel-icons"
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT, WORLD_WIDTH, WORLD_HEIGHT, TILE_SIZE, ITEM_PICKUP_RANGE } from "@/data/game-settings"
import { worldToTile, tileToWorldCenter } from "@/utils/tile-utils"
import { heroTypes } from "@/data/hero-types"
import { createEnemy } from "@/systems/enemy-system"
import { enemyTypes } from "@/data/enemy-types"
import { getItemDefinition, incrementBeaconCharges } from "@/systems/item-system"
import { cloneHero, loadClones } from "@/services/hero-clone-service"
import { getInterestBonus } from "@/systems/skill-system"
import { SpaceMapView } from "./map/space-map-view"
import { StarMapShopWindow } from "./shop/star-map-shop-window"
import { UpgradeTreeWindow } from "./shop/upgrade-tree-window"
import { AbductionScoreOverlay } from "./abduction-score-overlay"
import type { MapNode } from "@/types/map.types"
import { getUpgradeModifiers } from "@/systems/upgrade-system"
import { getHeroismCost } from "@/systems/shrine-system"
import { setRallyPoint, isWithinRallyRadius, forceSoldiersToRally } from "@/systems/barracks-system"
import { cn } from "@/lib/utils"
import { useBackgroundMusic } from "@/hooks/use-background-music"
import { useScreenMusic } from "@/hooks/use-screen-music"
import { playDroppodMenuSound, playAbductionSound } from "@/hooks/use-sound-effects"
import { TourOverlay } from "@/components/tour"
import { NineSliceButton } from "@/components/ui/nine-slice-button"
import { usePhasedAssetLoader } from "@/hooks/use-phased-asset-loader"
import { LoadingOverlay } from "@/components/ui/loading-overlay"
import { TutorialModal } from "@/components/game-ui/tutorial/tutorial-modal"
import { useTutorial } from "@/hooks/use-tutorial"
import { NewEnemyButton } from "@/components/game-ui/new-enemy/new-enemy-button"
import { NewEnemyCard } from "@/components/game-ui/new-enemy/new-enemy-card"
import { useNewEnemy } from "@/hooks/use-new-enemy"

const ZOOM_OUT = VIEWPORT_HEIGHT / WORLD_HEIGHT // 960 / 1920 = 0.5, shows full world vertically
const ZOOM_DEFAULT = ZOOM_OUT + (1 - ZOOM_OUT) * 0.85 // 0.85, 85% between full zoom-out and zoom-in

// Calculate minimum zoom based on viewport width - don't allow zooming out wider than the world
const getMinZoom = (viewportWidth: number) => {
  const minZoomForWidth = viewportWidth / WORLD_WIDTH // Minimum zoom to not exceed world width
  return Math.max(ZOOM_OUT, minZoomForWidth) // Use the more restrictive limit
}

// ZOOM_DEFAULT is a fixed design value, but on wide/landscape displays the
// canvas is wider than 0.85 zoom can cover, which let the page background show
// through the right-hand edge of the play area. Always respect the same lower
// bound the pinch/wheel handlers use.
const getDefaultZoom = (viewportWidth: number) => Math.max(ZOOM_DEFAULT, getMinZoom(viewportWidth))

// Upper bound for pinch and wheel zoom
const MAX_ZOOM = 2
const ZOOM_TRANSITION_MS = 300

export const Game = () => {
  const gameState = useGameState()
  const { isLandscape, isMobile, isDesktop, gameHeight, isPillarboxed, pillarWidth } = useViewport() // Added pillarWidth from useViewport
  const { isTutorialOpen, openTutorial, closeTutorial, hasSeenTutorial } = useTutorial()

  // Fast-forward toggle (1x/2x). The rate lives in the game clock so frame
  // ticks and wall-clock effect durations speed up together; this state only
  // mirrors it for the HUD chip.
  const playerPrefs = usePlayerPrefs()
  const [gameSpeed, setGameSpeed] = useState(() => (getPlayerPrefs().startFastForwarded ? 2 : 1))
  // Apply the preferred starting rate to the clock itself, not just the HUD chip
  useEffect(() => {
    setGameClockRate(gameSpeed)
    // Only on mount - later changes go through toggleGameSpeed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const toggleGameSpeed = useCallback(() => {
    setGameSpeed((prev) => {
      const next = prev > 1 ? 1 : 2
      setGameClockRate(next)
      return next
    })
  }, [])
  const { currentNotification, isCardOpen, notifyNewEnemy, openCard, closeCard } = useNewEnemy()

  // Reading the new-enemy card should not cost lives: pause the wave while it
  // is open and resume when it closes (only if we were the ones who paused).
  // Opt-out lives in the menu; turning it off mid-pause must still resume,
  // so the pref only gates the pause branch, never the release.
  const pausedForEnemyCardRef = useRef(false)
  useEffect(() => {
    const shouldHold = playerPrefs.pauseOnEnemyCard && isCardOpen
    if (shouldHold && gameState.gameState === "playing") {
      pausedForEnemyCardRef.current = true
      gameState.actions.pauseGame()
    } else if (!shouldHold && pausedForEnemyCardRef.current) {
      pausedForEnemyCardRef.current = false
      if (gameState.gameState === "paused") {
        gameState.actions.resumeGame()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCardOpen, gameState.gameState, playerPrefs.pauseOnEnemyCard])
  const { spendCrystals } = useCrystals()
  const { identityKey } = useAuth()

  // Unlock the codex entry for anything the hero is actually holding. Watching
  // the inventory rather than each acquisition path (shop, ground pickup, loot
  // node, starting kit) means no route can be missed; discoverMany() only
  // notifies when something is genuinely new.
  useEffect(() => {
    const held = gameState.heroInventory.slots
      .filter((slot): slot is NonNullable<typeof slot> => slot !== null)
      .map((slot) => slot.itemDefId)
    if (held.length > 0) discoverMany("items", held)
  }, [gameState.heroInventory])

  // Wall-clock start of the current run, for the local play-time stat
  const runStartedAtRef = useRef<number | null>(null)

  // Record the finished run: always to this device's local stats, and to the
  // identity-keyed leaderboard when a wallet is connected. Both fire once per
  // run; the server keeps the best score per key (only-improve upsert).
  const scoreSubmittedRef = useRef(false)
  const runRecordedRef = useRef(false)
  useEffect(() => {
    if (gameState.gameState === "playing") {
      scoreSubmittedRef.current = false
      runRecordedRef.current = false
      if (runStartedAtRef.current === null) runStartedAtRef.current = Date.now()
      return
    }
    const runEnded = gameState.gameState === "defeat" || gameState.gameState === "victory"
    if (!runEnded) return

    if (!runRecordedRef.current) {
      runRecordedRef.current = true
      recordRun({
        heroType: gameState.selectedHeroType,
        score: gameState.stats.score,
        levelIndex: gameState.currentLevelIndex,
        durationMs: runStartedAtRef.current ? Date.now() - runStartedAtRef.current : 0,
        won: gameState.gameState === "victory",
      })
      runStartedAtRef.current = null
    }

    if (identityKey && !scoreSubmittedRef.current && gameState.stats.score > 0) {
      scoreSubmittedRef.current = true
      // BRC-100-signed submission: the server verifies the wallet signature
      // and uses the signed pubkey, so scores can't be spoofed for other keys.
      signedFetch("/api/score", { score: gameState.stats.score }, identityKey).catch(() => {
        // Best effort - a lost submission only matters if it was a new best
      })
    }
  }, [
    gameState.gameState,
    gameState.selectedHeroType,
    gameState.currentLevelIndex,
    identityKey,
    gameState.stats.score,
  ])

  // ============================================
  // RUN RECOVERY (crash / tab-eviction snapshots)
  // ============================================
  // Latest game state by ref so the snapshot interval never has stale data
  const gameStateSnapRef = useRef(gameState)
  gameStateSnapRef.current = gameState

  // Pending run found in storage on load - offered on the title screen
  const [pendingRun, setPendingRun] = useState<PersistedGameState | null>(null)
  useEffect(() => {
    setPendingRun(loadPendingRun())
  }, [])

  // Snapshot the run every few seconds during play and on pagehide/tab-hide.
  // Enemies/projectiles are transient (restore re-runs the current wave), so
  // the payload is small and safe to write from a timer.
  useEffect(() => {
    const active = gameState.gameState === "playing" || gameState.gameState === "paused"
    if (!active) return

    const snapshot = () => {
      const g = gameStateSnapRef.current
      if (!g.hero || g.stats.lives <= 0) return
      savePendingRun({
        gameState: "playing", // always resume into gameplay, not a pause overlay
        stats: g.stats,
        towers: g.towers,
        hero: g.hero,
        selectedHeroType: g.selectedHeroType,
        currentLevelIndex: g.currentLevelIndex,
        currentWaveIndex: g.currentWaveIndex,
        showNextWaveButton: g.showNextWaveButton,
        freePremiumTowers: g.freePremiumTowers,
        landedPod: g.landedPod,
        summonedUnits: g.summonedUnits,
        heroInventory: g.heroInventory,
        upgradeState: g.upgradeState,
        mapState: g.mapState,
        heroismLevel: g.heroismLevel,
      })
    }

    const intervalId = setInterval(snapshot, 5000)
    const onHide = () => {
      if (document.visibilityState === "hidden") snapshot()
    }
    window.addEventListener("pagehide", snapshot)
    document.addEventListener("visibilitychange", onHide)
    return () => {
      clearInterval(intervalId)
      window.removeEventListener("pagehide", snapshot)
      document.removeEventListener("visibilitychange", onHide)
    }
  }, [gameState.gameState])

  // A lost run is not resumable
  useEffect(() => {
    if (gameState.gameState === "defeat") {
      clearPendingRun()
      setPendingRun(null)
    }
  }, [gameState.gameState])

  const handleResumeRun = useCallback(() => {
    if (!pendingRun) return
    gameState.actions.restoreGameState(pendingRun)
    setPendingRun(null)
  }, [pendingRun, gameState.actions])

  const handleDiscardRun = useCallback(() => {
    clearPendingRun()
    setPendingRun(null)
  }, [])

  // Starting a fresh game abandons any recoverable run
  const handleStartGame = useCallback(() => {
    clearPendingRun()
    setPendingRun(null)
    gameState.actions.startGame()
  }, [gameState.actions])

  // Fast-forward only applies to an active run - reset when leaving it so a
  // new run never starts silently sped up
  useEffect(() => {
    if (gameState.gameState !== "playing" && gameState.gameState !== "paused") {
      setGameSpeed(1)
      setGameClockRate(1)
    }
  }, [gameState.gameState])

  // Auto-pause when page is hidden to handle mobile RAF throttling
  usePageVisibility({
    onVisibilityChange: (isVisible) => {
      if (isVisible) {
        // Page became visible - resume if system-paused
        gameState.actions.resumeGameFromSystem()
      } else {
        // Page became hidden - auto-pause if playing
        if (gameState.gameState === "playing") {
          gameState.actions.pauseGameForSystem()
        }
      }
    },
  })

  // Phased asset loading for optimal performance
  const { state: assetState, loadPhase3, loadPhase4, isPhaseComplete } = usePhasedAssetLoader()

  // Load Phase 3 (hero + towers + level assets) when hero is selected and dropping
  useEffect(() => {
    if (gameState.gameState === "dropping" && gameState.hero && !isPhaseComplete(3)) {
      const heroType = gameState.hero.id as "paladin" | "ranger" | "siegesmith"
      const levelNumber = gameState.currentLevelIndex + 1
      loadPhase3(heroType, levelNumber)
    }
  }, [gameState.gameState, gameState.hero, gameState.currentLevelIndex, loadPhase3, isPhaseComplete])

  // Load Phase 4 (next level assets) during victory/shop screen
  useEffect(() => {
    if (gameState.gameState === "victory" && gameState.currentLevelIndex < 10) {
      const nextLevel = gameState.currentLevelIndex + 2 // +1 for 0-based, +1 for next level
      loadPhase4(nextLevel)
    }
  }, [gameState.gameState, gameState.currentLevelIndex, loadPhase4])

  // Reset landing sequence completion when entering landing state
  useEffect(() => {
    if (gameState.gameState === "landing") {
      setLandingSequenceCompleted(false)
    }
  }, [gameState.gameState])

  // Background music - plays during droppod, landing, and gameplay, stops during abduction or when map is open
  const isMusicPlaying = (
    gameState.gameState === "dropping" ||
    gameState.gameState === "landing" ||
    gameState.gameState === "playing"
  ) && !gameState.isMapOpen
  useBackgroundMusic({
    levelNumber: gameState.currentLevelIndex + 1, // currentLevelIndex is 0-based, level numbers are 1-based
    isPlaying: isMusicPlaying,
    isPaused: gameState.gameState === "paused",
    isAbducting: gameState.abductionAnimation !== null,
  })

  // Shop music - plays during victory/shop screen
  useScreenMusic({
    musicUrl: "/sounds/events/shop.mp3",
    isActive: gameState.gameState === "victory",
  })

  // Starmap music - plays when map is open
  useScreenMusic({
    musicUrl: "/sounds/events/starmap.mp3",
    isActive: gameState.isMapOpen,
  })
  const [selectedTileMenu, setSelectedTileMenu] = useState<{
    tilePosition: Position
    screenPosition: { x: number; y: number }
  } | null>(null)
  const [selectedDroppedItem, setSelectedDroppedItem] = useState<{
    itemId: string
    screenPosition: { x: number; y: number }
  } | null>(null)
  const [selectedDroppodMenu, setSelectedDroppodMenu] = useState<{
    screenPosition: { x: number; y: number }
  } | null>(null)
  const [selectedShrineMenu, setSelectedShrineMenu] = useState<{
    screenPosition: { x: number; y: number }
    shrineId: string
    shrineLevel: number
  } | null>(null)
  const [isPhaseTerminalOpen, setIsPhaseTerminalOpen] = useState(false)
  const [isStarMapShopOpen, setIsStarMapShopOpen] = useState(false)
  const [isUpgradeTreeOpen, setIsUpgradeTreeOpen] = useState(false)
  const [cloneCount, setCloneCount] = useState(0)
  const [showCloneParticles, setShowCloneParticles] = useState(false)
  const [showBeaconParticles, setShowBeaconParticles] = useState(false)
  const [cloneToast, setCloneToast] = useState<string | null>(null)
  const [showDroppodTutorial, setShowDroppodTutorial] = useState(true)
  const [showWaveTutorial, setShowWaveTutorial] = useState(true)
  const [landingSequenceCompleted, setLandingSequenceCompleted] = useState(false)
  const [ghostTower, setGhostTower] = useState<{
    type: TowerType
    position: Position
    valid: boolean
  } | null>(null)
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  // Surfaced through the bottom bar's toast. It used to be set and never
  // rendered, so a premium tower you could not afford silently did nothing.
  const [paymentError, setPaymentError] = useState<string | null>(null)
  // Removed useViewportHeight import and usage
  const [lastClickedTile, setLastClickedTile] = useState<{
    x: number
    y: number
  } | null>(null)
  const [viewport, setViewport] = useState<ViewportState>({
    x: 0,
    y: 0,
    width: VIEWPORT_WIDTH,
    height: VIEWPORT_HEIGHT, // Use constant initially, update via useEffect
    zoom: ZOOM_DEFAULT,
  })
  // Ref mirror of viewport so event handlers/effects can read the latest value
  // without adding viewport to their dependency arrays
  const viewportRef = useRef(viewport)
  useEffect(() => {
    viewportRef.current = viewport
  }, [viewport])
  const [isManualViewport, setIsManualViewport] = useState(false)
  const [isAnimatingZoom, setIsAnimatingZoom] = useState(false)
  const [canvasRightOffset, setCanvasRightOffset] = useState(0) // Distance from canvas right edge to screen edge
  // Mobile skill targeting: first tap shows preview, second tap executes
  const [mobileTargetPreviewPos, setMobileTargetPreviewPos] = useState<Position | null>(null)
  const [targetZoom, setTargetZoom] = useState(ZOOM_DEFAULT)
  const animationFrameRef = useRef<number | null>(null)
  const manualViewportTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const pinchStartZoomRef = useRef<number>(ZOOM_DEFAULT)
  const didPinchRef = useRef<boolean>(false) // set during pinch-move, decides snap on touch end
  const debugSpawnTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())

  // Cancel pending debug enemy spawns on unmount
  useEffect(() => {
    const timeouts = debugSpawnTimeoutsRef.current
    return () => {
      for (const id of timeouts) clearTimeout(id)
      timeouts.clear()
    }
  }, [])
  const lastTouchDistanceRef = useRef<number | null>(null)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const isPanningRef = useRef<boolean>(false)
  const lastPanPositionRef = useRef<{ x: number; y: number } | null>(null)
  const keysPressed = useRef<Set<string>>(new Set())
  const isSpacePressedRef = useRef<boolean>(false)
  const spaceMouseStartRef = useRef<{ x: number; y: number } | null>(null)
  const heroRef = useRef(gameState.hero)
  const actionsRef = useRef(gameState.actions)
  const touchStartTimeRef = useRef<number>(0)
  const touchStartPositionRef = useRef<{ x: number; y: number } | null>(null)
  const hasPannedRef = useRef(false)
  const debugLevelRef = useRef(0)
  const hasCheckedSavedStateRef = useRef(false)

  // Refs for stable function references in animation phase progression
  const completeAnimationRef = useRef<() => void>(() => {})
  const setAnimationRef = useRef<(updater: (prev: AbductionAnimationState | null) => AbductionAnimationState | null) => void>(() => {})
  const abductionOriginalZoomRef = useRef<number | null>(null)

  useEffect(() => {
    setViewport((prev) => ({
      ...prev,
      height: gameHeight,
    }))
  }, [gameHeight])

  // Update viewport width for full-width desktop mode
  useEffect(() => {
    const updateViewportWidth = () => {
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      const isDesktopLandscape = isDesktop && isPillarboxed

      if (isDesktopLandscape) {
        // Full-width mode: calculate wider viewport based on screen aspect ratio
        const aspectRatio = screenWidth / screenHeight
        const newWidth = Math.floor(VIEWPORT_HEIGHT * aspectRatio)
        // Cap at WORLD_WIDTH to prevent viewport wider than world
        const cappedWidth = Math.min(newWidth, WORLD_WIDTH)
        setViewport((prev) => {
          // A wider viewport raises the minimum zoom that still covers the
          // world; without this the page background shows through the right
          // edge of the play area after a resize/orientation change.
          const minZoom = getMinZoom(cappedWidth)
          const zoom = Math.max(prev.zoom, minZoom)
          const viewWidth = cappedWidth / zoom
          const viewHeight = prev.height / zoom
          return {
            ...prev,
            width: cappedWidth,
            zoom,
            x: Math.max(0, Math.min(WORLD_WIDTH - viewWidth, prev.x)),
            y: Math.max(0, Math.min(WORLD_HEIGHT - viewHeight, prev.y)),
          }
        })
        setTargetZoom((prev) => Math.max(prev, getMinZoom(cappedWidth)))

        // Calculate canvas right offset for ultra-wide monitors
        // When capped, the canvas is narrower than the screen and centered
        if (newWidth > WORLD_WIDTH) {
          // Canvas display width = screenHeight * (WORLD_WIDTH / VIEWPORT_HEIGHT)
          const canvasDisplayWidth = screenHeight * (WORLD_WIDTH / VIEWPORT_HEIGHT)
          const offset = Math.floor((screenWidth - canvasDisplayWidth) / 2)
          setCanvasRightOffset(offset)
        } else {
          setCanvasRightOffset(0)
        }
      } else {
        // Standard mode: use design viewport width
        setViewport((prev) => ({
          ...prev,
          width: VIEWPORT_WIDTH,
        }))
        setCanvasRightOffset(0)
      }
    }

    updateViewportWidth()
    window.addEventListener("resize", updateViewportWidth)
    window.addEventListener("orientationchange", updateViewportWidth)

    return () => {
      window.removeEventListener("resize", updateViewportWidth)
      window.removeEventListener("orientationchange", updateViewportWidth)
    }
  }, [isDesktop, isPillarboxed])

  // Enable custom cursor on desktop
  useEffect(() => {
    if (isDesktop) {
      document.body.classList.add('custom-cursor-enabled')
    } else {
      document.body.classList.remove('custom-cursor-enabled')
    }
    return () => {
      document.body.classList.remove('custom-cursor-enabled')
    }
  }, [isDesktop])

  // Sync clone count whenever game state changes (especially when starting a new game)
  useEffect(() => {
    setCloneCount(loadClones().length)
  }, [gameState.gameState])

  // Close upgrade tree when starting a new level
  useEffect(() => {
    if (gameState.gameState === "landing" || gameState.gameState === "playing") {
      setIsUpgradeTreeOpen(false)
    }
  }, [gameState.gameState])

  // Show toast when clone restore effect is triggered
  useEffect(() => {
    if (gameState.cloneRestoreEffect.active && gameState.cloneRestoreEffect.restoredLevel !== null) {
      const level = gameState.cloneRestoreEffect.restoredLevel
      setCloneToast(`Clone Activated!\nRestored to Level ${level}`)

      // Update clone count after restoration
      setCloneCount(loadClones().length)

      // Trigger blue clone particles
      setShowCloneParticles(true)

      // Auto-hide toast after 4 seconds
      const timeout = setTimeout(() => {
        setCloneToast(null)
      }, 4000)

      // Clear particles after animation
      const particleTimeout = setTimeout(() => {
        setShowCloneParticles(false)
      }, 2000)

      return () => {
        clearTimeout(timeout)
        clearTimeout(particleTimeout)
      }
    }
  }, [gameState.cloneRestoreEffect])

  // Close dropped item menu if the selected item no longer exists
  useEffect(() => {
    if (selectedDroppedItem) {
      const itemExists = gameState.droppedItems.some((item) => item.id === selectedDroppedItem.itemId)
      if (!itemExists) {
        setSelectedDroppedItem(null)
      }
    }
  }, [selectedDroppedItem, gameState.droppedItems])

  // Restore a saved game snapshot if one exists (legacy auth-roundtrip path)
  useEffect(() => {
    if (hasCheckedSavedStateRef.current) return
    hasCheckedSavedStateRef.current = true

    const savedState = loadGameStateAfterAuth()
    if (savedState) {
      console.log("[Game] Restoring saved game state after auth")
      gameState.actions.restoreGameState(savedState)
      clearSavedGameState()
    }
  }, [gameState.actions])

  // Don't automatically trigger abduction - wait for player to click "Visit shop" button
  // useEffect(() => {
  //   if (gameState.isLastWaveComplete && !gameState.abductionAnimation) {
  //     gameState.actions.startAbductionAnimation()
  //   }
  // }, [gameState.isLastWaveComplete, gameState.abductionAnimation, gameState.actions])

  // Keep refs updated with latest function references (avoids stale closure issues)
  useEffect(() => {
    completeAnimationRef.current = gameState.actions.completeAbductionAnimation
    setAnimationRef.current = gameState.setters.setAbductionAnimation
  }, [gameState.actions.completeAbductionAnimation, gameState.setters.setAbductionAnimation])

  // Animation phase progression - uses refs to avoid stale closure issues
  useEffect(() => {
    if (!gameState.abductionAnimation) return

    const PHASE_DURATIONS: Record<AbductionPhase, number> = {
      camera_zoom: 500,
      beam_fade_in: 1000,
      hero_ascend: 2000,
      beam_fade_out: 500,
      score_count: 2000,
      score_display: 1000, // 1 second delay after score finishes counting
      complete: 0,
    }

    const PHASE_ORDER: AbductionPhase[] = [
      "camera_zoom",
      "beam_fade_in",
      "hero_ascend",
      "beam_fade_out",
      "score_count",
      "score_display",
      "complete",
    ]

    const currentPhase = gameState.abductionAnimation.phase
    const phaseStartTime = gameState.abductionAnimation.phaseStartTime
    const duration = PHASE_DURATIONS[currentPhase]

    const checkPhaseTransition = () => {
      const elapsed = gameNow() - phaseStartTime

      if (elapsed >= duration) {
        const currentIndex = PHASE_ORDER.indexOf(currentPhase)
        const nextPhase = PHASE_ORDER[currentIndex + 1]

        if (nextPhase === "complete") {
          // Use ref to get the latest function reference
          completeAnimationRef.current()
        } else if (nextPhase) {
          // Use ref to get the latest setter reference
          setAnimationRef.current((prev) =>
            prev ? { ...prev, phase: nextPhase, phaseStartTime: gameNow() } : null
          )
        }
      }
    }

    const interval = setInterval(checkPhaseTransition, 50)
    return () => clearInterval(interval)
  }, [gameState.abductionAnimation?.phase, gameState.abductionAnimation?.phaseStartTime])

  // Camera zoom to medium level during abduction animation
  useEffect(() => {
    if (gameState.abductionAnimation?.phase === "camera_zoom") {
      // Store the original zoom level before changing it
      if (abductionOriginalZoomRef.current === null) {
        abductionOriginalZoomRef.current = viewport.zoom
      }
      const MEDIUM_ZOOM = 0.9
      setTargetZoom(MEDIUM_ZOOM)
      setIsAnimatingZoom(true)
    }
  }, [gameState.abductionAnimation?.phase, viewport.zoom])

  // Restore original zoom when abduction completes
  useEffect(() => {
    // When abduction animation ends (goes from active to null), restore original zoom
    if (!gameState.abductionAnimation && abductionOriginalZoomRef.current !== null) {
      setTargetZoom(abductionOriginalZoomRef.current)
      setIsAnimatingZoom(true)
      abductionOriginalZoomRef.current = null // Reset for next abduction
    }
  }, [gameState.abductionAnimation])

  // Play abduction sound when beam starts (0.5 seconds into animation)
  useEffect(() => {
    if (gameState.abductionAnimation?.phase === "beam_fade_in") {
      playAbductionSound()
    }
  }, [gameState.abductionAnimation?.phase])

  // Removed checkOrientation effect, now handled by useViewport
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()
      if (["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"].includes(key)) {
        keysPressed.current.add(key)
      }

      // Space bar for panning (desktop only)
      if (e.key === " " && isDesktop) {
        e.preventDefault() // Prevent page scroll
        isSpacePressedRef.current = true
      }

      // Dev-only: skip to victory. Must never ship live - it lets anyone
      // fast-forward runs and post unearned leaderboard scores.
      if (e.key === "]" && process.env.NODE_ENV !== "production") {
        console.log("[v0] Debug: Triggering victory for level", debugLevelRef.current + 1)

        // Set the level and show victory screen
        gameState.setters.setGameState("victory")

        // Update the current level index to match the debug level
        // This ensures the victory screen shows the correct level info
        const targetLevel = debugLevelRef.current
        if (targetLevel < gameState.totalLevels) {
          // Increment for next press
          debugLevelRef.current = (debugLevelRef.current + 1) % gameState.totalLevels
        }
      }

      // Skill hotkeys (1-4)
      if (["1", "2", "3", "4"].includes(e.key) && gameState.gameState === "playing") {
        gameState.actions.useSkillByHotkey(e.key)
        // Clear mobile preview when switching skills
        setMobileTargetPreviewPos(null)
      }

      // Cancel pending skill or item with Escape
      if (e.key === "Escape") {
        if (gameState.pendingSkillId) {
          gameState.actions.cancelPendingSkill()
          setMobileTargetPreviewPos(null)
        }
        if (gameState.pendingItemSlot !== null) {
          gameState.actions.cancelPendingItem()
          setMobileTargetPreviewPos(null)
        }
      }

      // Open map with 'L' key
      if (key === "l" && gameState.gameState === "playing") {
        gameState.actions.openMap()
      }

      // DEBUG (dev-only): Press 'J' to spawn one of each enemy type at spawn
      // point with 2s delay - also sets gold to 10k, so it must not ship live
      if (key === "j" && gameState.gameState === "playing" && process.env.NODE_ENV !== "production") {
        console.log("[DEBUG] Spawning all enemy types for sprite direction testing")
        const allEnemyTypes = Object.keys(enemyTypes)
        const spawnPos = gameState.level.path[0]

        // Clear existing enemies and set gold
        gameState.setters.setEnemies([])
        gameState.setters.setStats((prev) => ({ ...prev, money: 10000 }))

        // Spawn enemies with 2 second delay between each (tracked so they
        // can be cancelled on unmount/restart instead of firing into a new game)
        allEnemyTypes.forEach((enemyType, index) => {
          const timeoutId = setTimeout(() => {
            debugSpawnTimeoutsRef.current.delete(timeoutId)
            const enemy = createEnemy(enemyType, spawnPos, 0, 0)
            gameState.setters.setEnemies((prev) => [...prev, enemy])
            console.log("[DEBUG] Spawned", enemyType, "at spawn point")
          }, index * 2000) // 2 second delay between each
          debugSpawnTimeoutsRef.current.add(timeoutId)
        })

        console.log("[DEBUG] Will spawn", allEnemyTypes.length, "enemies:", allEnemyTypes.join(", "))
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()
      keysPressed.current.delete(key)

      // Space bar release
      if (e.key === " ") {
        isSpacePressedRef.current = false
        spaceMouseStartRef.current = null
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
    }
  }, [gameState])

  useEffect(() => {
    if (!isAnimatingZoom) return

    // Read the starting zoom from the ref, NOT viewport.zoom deps - otherwise
    // each setViewport re-runs this effect, restarting the easing every frame
    const startZoom = viewportRef.current.zoom
    const startTime = performance.now()

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / ZOOM_TRANSITION_MS, 1)
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3)

      const newZoom = startZoom + (targetZoom - startZoom) * eased

      setViewport((prev) => {
        // When zooming, keep centered on hero if selected, otherwise keep current center
        const centerX = prev.x + prev.width / prev.zoom / 2
        const centerY = prev.y + prev.height / prev.zoom / 2

        const newViewWidth = prev.width / newZoom
        const newViewHeight = prev.height / newZoom

        let newX = centerX - newViewWidth / 2
        let newY = centerY - newViewHeight / 2

        newX = Math.max(0, Math.min(WORLD_WIDTH - newViewWidth, newX))
        newY = Math.max(0, Math.min(WORLD_HEIGHT - newViewHeight, newY))

        return {
          ...prev,
          x: newX,
          y: newY,
          zoom: newZoom,
        }
      })

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate)
      } else {
        setIsAnimatingZoom(false)
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isAnimatingZoom, targetZoom])

  useEffect(() => {
    // During abduction animation, force camera to follow hero
    const isAbductionActive = gameState.abductionAnimation &&
      ["camera_zoom", "beam_fade_in", "hero_ascend"].includes(gameState.abductionAnimation.phase)

    if (!gameState.hero || !gameState.hero.position) return

    // Normal follow conditions (skip if abduction is forcing follow)
    if (!isAbductionActive) {
      if (isManualViewport || isAnimatingZoom) return
      if (!gameState.selectedHeroId) return
      if (viewport.zoom < ZOOM_DEFAULT * 0.9) return // Don't follow when zoomed out
      if (isDesktop) return // Don't auto-follow on desktop
    }

    const heroX = gameState.hero.position.x
    const heroY = gameState.hero.position.y

    // Skip if hero position is invalid (0,0 or undefined)
    if (heroX === 0 && heroY === 0) return

    const viewWidth = viewport.width / viewport.zoom
    const viewHeight = viewport.height / viewport.zoom

    // Center viewport on hero
    const targetX = heroX - viewWidth / 2
    const targetY = heroY - viewHeight / 2

    // Clamp to world bounds (not viewport bounds)
    const clampedX = Math.max(0, Math.min(WORLD_WIDTH - viewWidth, targetX))
    const clampedY = Math.max(0, Math.min(WORLD_HEIGHT - viewHeight, targetY))

    setViewport((prev) => ({
      ...prev,
      x: clampedX,
      y: clampedY,
    }))
  }, [
    gameState.hero,
    gameState.selectedHeroId,
    gameState.abductionAnimation,
    isManualViewport,
    isAnimatingZoom,
    viewport.width,
    viewport.height,
    viewport.zoom,
  ])

  useEffect(() => {
    heroRef.current = gameState.hero
  }, [gameState.hero])

  useEffect(() => {
    actionsRef.current = gameState.actions
  }, [gameState.actions])

  useEffect(() => {
    if (gameState.gameState !== "playing") return

    let animFrame: number
    let lastTime = performance.now()

    const processMovement = (currentTime: number) => {
      const delta = (currentTime - lastTime) / 1000
      lastTime = currentTime

      const hero = heroRef.current
      const actions = actionsRef.current

      if (keysPressed.current.size > 0 && hero && hero.position) {
        let dx = 0
        let dy = 0

        if (keysPressed.current.has("w") || keysPressed.current.has("arrowup")) dy -= 1
        if (keysPressed.current.has("s") || keysPressed.current.has("arrowdown")) dy += 1
        if (keysPressed.current.has("a") || keysPressed.current.has("arrowleft")) dx -= 1
        if (keysPressed.current.has("d") || keysPressed.current.has("arrowright")) dx += 1

        if (dx !== 0 || dy !== 0) {
          const length = Math.sqrt(dx * dx + dy * dy)
          dx /= length
          dy /= length

          // Use hero's actual speed instead of constant
          const moveDistance = hero.speed * delta
          const newX = hero.position.x + dx * moveDistance
          const newY = hero.position.y + dy * moveDistance

          // Clamp to world bounds with margin for hero size
          const margin = 32
          const clampedX = Math.max(margin, Math.min(WORLD_WIDTH - margin, newX))
          const clampedY = Math.max(margin, Math.min(WORLD_HEIGHT - margin, newY))

          actions.moveHero({ x: clampedX, y: clampedY })

          // Center viewport on hero while using keyboard
          setIsManualViewport(false)
        }
      }

      animFrame = requestAnimationFrame(processMovement)
    }

    animFrame = requestAnimationFrame(processMovement)

    return () => {
      cancelAnimationFrame(animFrame)
    }
  }, [gameState.gameState]) // Only depend on gameState, use refs for hero/actions

  const handleViewportChange = useCallback((position: Position) => {
    setViewport((prev) => ({
      ...prev,
      x: position.x,
      y: position.y,
    }))
    setIsManualViewport(true)

    // Clear existing timeout
    if (manualViewportTimeoutRef.current) {
      clearTimeout(manualViewportTimeoutRef.current)
    }

    // Reset manual mode after 3 seconds if hero is selected
    manualViewportTimeoutRef.current = setTimeout(() => {
      setIsManualViewport(false)
    }, 3000)
  }, [])

  const handleZoomOut = useCallback(() => {
    const minZoom = getMinZoom(viewport.width)
    setTargetZoom(minZoom)
    setIsAnimatingZoom(true)
    setIsManualViewport(true)
  }, [viewport.width])

  const handleZoomIn = useCallback(() => {
    const defaultZoom = getDefaultZoom(viewport.width)
    setTargetZoom(defaultZoom)
    setIsAnimatingZoom(true)

    if (gameState.hero && gameState.hero.position && (gameState.hero.position.x !== 0 || gameState.hero.position.y !== 0)) {
      const heroX = gameState.hero.position.x
      const heroY = gameState.hero.position.y
      const viewWidth = viewport.width / defaultZoom
      const viewHeight = viewport.height / defaultZoom

      const targetX = heroX - viewWidth / 2
      const targetY = heroY - viewHeight / 2

      // Clamp to world bounds
      const clampedX = Math.max(0, Math.min(WORLD_WIDTH - viewWidth, targetX))
      const clampedY = Math.max(0, Math.min(WORLD_HEIGHT - viewHeight, targetY))

      setViewport((prev) => ({
        ...prev,
        x: clampedX,
        y: clampedY,
      }))
    }

    setIsManualViewport(false)
  }, [gameState.hero, viewport.width, viewport.height])

  // Desktop: Select hero without moving camera (just show white border on portrait)
  const handleSelectHeroDesktop = useCallback(() => {
    if (gameState.hero && gameState.hero.position && (gameState.hero.position.x !== 0 || gameState.hero.position.y !== 0)) {
      // Toggle selection: if already selected, deselect; otherwise select
      if (gameState.selectedHeroId === gameState.hero.id) {
        gameState.actions.setSelectedHeroId(null)
      } else {
        gameState.actions.setSelectedHeroId(gameState.hero.id)
        gameState.actions.setSelectedTowerId(null)
        gameState.actions.deselectEnemy()
        gameState.actions.deselectSummon()
        gameState.actions.deselectSoldier()
        setSelectedTileMenu(null)
        setGhostTower(null)
      }
    }
  }, [gameState.hero, gameState.selectedHeroId, gameState.actions])

  const handleStartWave = useCallback(() => {
    console.log("[v0] handleStartWave: Called, currentWaveIndex:", gameState.currentWaveIndex)

    if (gameState.currentWaveIndex === 0 && gameState.waveSpawnerRef.current.totalSpawned === 0) {
      console.log("[v0] handleStartWave: Calling startFirstWave")
      gameState.actions.startFirstWave()
    } else {
      console.log("[v0] handleStartWave: Calling startNextWave")
      gameState.actions.startNextWave()
    }
  }, [gameState.currentWaveIndex, gameState.waveSpawnerRef, gameState.actions])

  // Handle new enemy notifications, and permanently unlock the codex entry.
  // discover() is a no-op after the first sighting, so this stays cheap on the
  // spawn path even though it runs for every enemy.
  const handleEnemySpawn = useCallback(
    (enemy: { type: string; variant: "regular" | "elite" | "unique" }) => {
      discover("enemies", enemy.type)
      notifyNewEnemy(enemy.type, enemy.variant)
    },
    [notifyNewEnemy]
  )

  useGameLoop({
    gameState: gameState.gameState,
    enemies: gameState.enemies,
    towers: gameState.towers,
    hero: gameState.hero,
    projectiles: gameState.projectiles,
    skillEffects: gameState.skillEffects,
    summonedUnits: gameState.summonedUnits,
    rootsEffects: gameState.rootsEffects,
    fireEffects: gameState.fireEffects,
    corpses: gameState.corpses,
    heroInventory: gameState.heroInventory,
    activeItemEffects: gameState.activeItemEffects,
    aoeVisualEffects: gameState.aoeVisualEffects,
    pendingAoeWaves: gameState.pendingAoeWaves,
    laserBeamEffects: gameState.laserBeamEffects,
    barracksSoldiers: gameState.barracksSoldiers,
    soldierSpawnPortraits: gameState.soldierSpawnPortraits,
    particleEffects: gameState.particleEffects,
    stats: gameState.stats,
    level: gameState.level,
    waveSpawner: gameState.waveSpawner,
    waveSpawnerRef: gameState.waveSpawnerRef,
    clearEnemiesRef: gameState.clearEnemiesRef,
    currentWaveIndex: gameState.currentWaveIndex,
    landedPod: gameState.landedPod,
    isDroppodSelected: !!selectedDroppodMenu,
    upgradeState: gameState.upgradeState,
    heroismLevel: gameState.heroismLevel,
    onEnemySpawn: handleEnemySpawn,
    setters: gameState.setters,
  })

  const getCanvasPosition = useCallback(
    (clientX: number, clientY: number): Position => {
      if (!canvasContainerRef.current) return { x: 0, y: 0 }
      const canvas = canvasContainerRef.current.querySelector("canvas") as HTMLCanvasElement | null
      if (!canvas) return { x: 0, y: 0 }
      const rect = canvas.getBoundingClientRect()

      // Use actual canvas dimensions for proper coordinate mapping
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height

      // Screen -> Viewport -> World coordinates
      const viewportX = (clientX - rect.left) * scaleX
      const viewportY = (clientY - rect.top) * scaleY

      return {
        x: viewportX / viewport.zoom + viewport.x,
        y: viewportY / viewport.zoom + viewport.y,
      }
    },
    [viewport],
  )

  const handleTapAtPosition = useCallback(
    (clientX: number, clientY: number) => {
      if (gameState.gameState !== "playing" && !gameState.showNextWaveButton) return
      if (isProcessingPayment) return
      // Block input during abduction animation
      if (gameState.abductionAnimation) return

      const worldPos = getCanvasPosition(clientX, clientY)
      const tile = worldToTile(worldPos.x, worldPos.y)
      const tileCenter = tileToWorldCenter(tile.tileX, tile.tileY)

      // If there's a pending targetable skill, handle targeting
      if (gameState.pendingSkillId) {
        // Check if clicked on an enemy (for targeted-dot skills like Entangling Roots)
        let clickedEnemyId: string | undefined
        for (const enemy of gameState.enemies) {
          if (pointInCircle(worldPos, enemy.position, enemy.size)) {
            clickedEnemyId = enemy.id
            break
          }
        }

        // On mobile: first tap shows preview, second tap executes
        // On desktop: mouse moves show preview, click executes
        if (!isDesktop && !mobileTargetPreviewPos) {
          // Mobile first tap: show preview at this position
          setMobileTargetPreviewPos(worldPos)
          return
        }

        // Execute the skill (desktop click or mobile second tap)
        gameState.actions.executeTargetedSkill(worldPos, clickedEnemyId)
        setSelectedTileMenu(null)
        setGhostTower(null)
        setMobileTargetPreviewPos(null)
        return
      }

      // If there's a pending targetable item (teleport, lure), handle targeting
      if (gameState.pendingItemSlot !== null) {
        // On mobile: first tap shows preview, second tap executes
        // On desktop: click executes immediately
        if (!isDesktop && !mobileTargetPreviewPos) {
          // Mobile first tap: show preview at this position
          setMobileTargetPreviewPos(worldPos)
          return
        }

        // Execute the item (desktop click or mobile second tap)
        gameState.actions.executeTargetedItem(worldPos)
        setSelectedTileMenu(null)
        setGhostTower(null)
        setMobileTargetPreviewPos(null)
        return
      }

      // Check if tapped on a dropped item
      for (const droppedItem of gameState.droppedItems) {
        if (pointInCircle(worldPos, droppedItem.position, 20)) {
          // Check if hero is within pickup range
          const heroPos = gameState.hero?.position
          if (heroPos) {
            const distToItem = Math.sqrt(
              (heroPos.x - droppedItem.position.x) ** 2 +
              (heroPos.y - droppedItem.position.y) ** 2
            )

            if (distToItem > ITEM_PICKUP_RANGE) {
              // Too far - move hero toward item instead of opening menu
              gameState.actions.moveHero(droppedItem.position)
              return
            }
          }

          // Within range - calculate screen position for the menu
          const canvas = canvasContainerRef.current?.querySelector("canvas") as HTMLCanvasElement | null
          if (canvas) {
            const rect = canvas.getBoundingClientRect()
            const scaleX = rect.width / canvas.width
            const scaleY = rect.height / canvas.height
            const screenX = rect.left + (droppedItem.position.x - viewport.x) * viewport.zoom * scaleX
            const screenY = rect.top + (droppedItem.position.y - viewport.y) * viewport.zoom * scaleY

            setSelectedDroppedItem({
              itemId: droppedItem.id,
              screenPosition: { x: screenX, y: screenY },
            })
            setSelectedTileMenu(null)
            setGhostTower(null)
          }
          return
        }
      }

      // Close dropped item menu if clicking elsewhere
      if (selectedDroppedItem) {
        setSelectedDroppedItem(null)
      }

      // Check if tapped on enemy
      for (const enemy of gameState.enemies) {
        if (pointInCircle(worldPos, enemy.position, enemy.size)) {
          gameState.actions.selectEnemy(enemy.id)
          gameState.actions.setSelectedTowerId(null)
          gameState.actions.deselectSummon()
          gameState.actions.deselectSoldier()
          setSelectedTileMenu(null)
          setGhostTower(null)
          return
        }
      }

      // Check if tapped on summoned unit (wolf or siege tower)
      for (const summon of gameState.summonedUnits) {
        if (pointInCircle(worldPos, summon.position, summon.size / 2)) {
          gameState.actions.selectSummon(summon.id)
          gameState.actions.setSelectedTowerId(null)
          gameState.actions.setSelectedHeroId(null)
          gameState.actions.deselectEnemy()
          gameState.actions.deselectSoldier()
          setSelectedTileMenu(null)
          setGhostTower(null)
          return
        }
      }

      // Check if tapped on barracks soldier
      for (const soldier of gameState.barracksSoldiers) {
        if (!soldier.isDead && pointInCircle(worldPos, soldier.position, 16)) {
          gameState.actions.selectSoldier(soldier.id)
          gameState.actions.setSelectedTowerId(null)
          gameState.actions.setSelectedHeroId(null)
          gameState.actions.deselectEnemy()
          gameState.actions.deselectSummon()
          setSelectedTileMenu(null)
          setGhostTower(null)
          return
        }
      }

      // Check if tapped on hero (use bounding box matching the white selection box)
      // Selection box is 64x64 (offset 32 from center), with yOffset of 4
      const heroHitboxSize = 32
      const heroYOffset = 4
      const heroHit = gameState.hero && gameState.hero.position && (
        worldPos.x >= gameState.hero.position.x - heroHitboxSize &&
        worldPos.x <= gameState.hero.position.x + heroHitboxSize &&
        worldPos.y >= gameState.hero.position.y - heroHitboxSize + heroYOffset &&
        worldPos.y <= gameState.hero.position.y + heroHitboxSize + heroYOffset
      )
      if (heroHit && gameState.hero) {
        gameState.actions.setSelectedHeroId(gameState.hero.id)
        gameState.actions.setSelectedTowerId(null)
        gameState.actions.deselectEnemy()
        gameState.actions.deselectSummon()
        gameState.actions.deselectSoldier()
        setSelectedTileMenu(null)
        setGhostTower(null)
        // On mobile, center camera on hero; on desktop, keep manual viewport control
        if (!isDesktop) {
          setIsManualViewport(false)
        }
        return
      }

      // Mobile: if barracks is selected, tap within rally radius moves rally point and deselects
      if (!isDesktop && gameState.selectedTowerId) {
        const selectedTower = gameState.towers.find((t) => t.id === gameState.selectedTowerId)
        if (selectedTower?.type === "barracks" && selectedTower.barracksState) {
          // Check if tap is within rally radius but NOT on the tower itself
          if (isWithinRallyRadius(selectedTower.position, worldPos) && !pointInCircle(worldPos, selectedTower.position, 32)) {
            const updatedTower = setRallyPoint(selectedTower, worldPos)
            gameState.setters.setTowers((prev) =>
              prev.map((t) => (t.id === selectedTower.id ? updatedTower : t))
            )
            // Force soldiers to stop attacking and move to new rally point for 2 seconds
            gameState.setters.setBarracksSoldiers((prev) =>
              forceSoldiersToRally(prev, selectedTower.id, 2000)
            )
            // Deselect the barracks after setting rally point
            gameState.actions.setSelectedTowerId(null)
            return
          }
        }
      }

      // Check if tapped on tower
      for (const tower of gameState.towers) {
        if (pointInCircle(worldPos, tower.position, 32)) {
          // Select tower (including shrines) - shows TowerInfoPanel for upgrading
          gameState.actions.setSelectedTowerId(tower.id)
          gameState.actions.setSelectedHeroId(null)
          gameState.actions.deselectEnemy()
          gameState.actions.deselectSummon()
          gameState.actions.deselectSoldier()
          setSelectedTileMenu(null)
          setSelectedDroppodMenu(null)
          setGhostTower(null)

          // If this is a shrine tower, also open the shrine menu
          if (tower.type === "shrine") {
            // Calculate screen position for the shrine menu
            const canvas = canvasContainerRef.current?.querySelector("canvas") as HTMLCanvasElement | null
            if (canvas) {
              const rect = canvas.getBoundingClientRect()
              const scaleX = rect.width / canvas.width
              const scaleY = rect.height / canvas.height

              // Convert world position to screen position (same transform as droppod menu)
              const screenX = rect.left + (tower.position.x - viewport.x) * viewport.zoom * scaleX
              const screenY = rect.top + (tower.position.y - viewport.y) * viewport.zoom * scaleY

              setSelectedShrineMenu({
                screenPosition: { x: screenX, y: screenY },
                shrineId: tower.id,
                shrineLevel: tower.level,
              })
            }
          }

          return
        }
      }

      // Check if tapped on landed droppod
      if (gameState.landedPod && gameState.landedPod.position) {
        const podPos = gameState.landedPod.position
        if (pointInCircle(worldPos, podPos, TILE_SIZE / 2)) {
          // Dismiss tutorial on first click
          if (showDroppodTutorial) {
            setShowDroppodTutorial(false)
          }
          // Calculate screen position for the menu
          const canvas = canvasContainerRef.current?.querySelector("canvas") as HTMLCanvasElement | null
          if (canvas) {
            const rect = canvas.getBoundingClientRect()
            const scaleX = rect.width / canvas.width
            const scaleY = rect.height / canvas.height
            const screenX = rect.left + (podPos.x - viewport.x) * viewport.zoom * scaleX
            // Center radial menu exactly on the droppod
            const screenY = rect.top + (podPos.y - viewport.y) * viewport.zoom * scaleY

            setSelectedDroppodMenu({
              screenPosition: { x: screenX, y: screenY },
            })
            playDroppodMenuSound()
            setSelectedTileMenu(null)
            setSelectedDroppedItem(null)
            setGhostTower(null)
          }
          return
        }
      }

      // Close droppod menu if clicking elsewhere
      if (selectedDroppodMenu) {
        setSelectedDroppodMenu(null)
      }

      // Close shrine menu if clicking elsewhere
      if (selectedShrineMenu) {
        setSelectedShrineMenu(null)
      }

      const isOnPath = isTileOnPath(tile.tileX, tile.tileY, gameState.level.path, gameState.level.pathWidth, TILE_SIZE)

      // Check if tile is directly on the droppod
      const isOnLandedPod =
        gameState.landedPod && tile.tileX === gameState.landedPod.tileX && tile.tileY === gameState.landedPod.tileY

      // Check if tile is on droppod or adjacent to it (up, down, left, right) - blocks building
      const isOnOrAdjacentToLandedPod = gameState.landedPod && (
        (tile.tileX === gameState.landedPod.tileX && tile.tileY === gameState.landedPod.tileY) || // On pod
        (tile.tileX === gameState.landedPod.tileX && tile.tileY === gameState.landedPod.tileY - 1) || // Above
        (tile.tileX === gameState.landedPod.tileX && tile.tileY === gameState.landedPod.tileY + 1) || // Below
        (tile.tileX === gameState.landedPod.tileX - 1 && tile.tileY === gameState.landedPod.tileY) || // Left
        (tile.tileX === gameState.landedPod.tileX + 1 && tile.tileY === gameState.landedPod.tileY) // Right
      )

      const canBuild =
        canPlaceTower(tileCenter, "archer", gameState.towers, gameState.level.path, gameState.level.pathWidth, gameState.level.tilemap?.objectDecorations, gameState.level.tilemap?.decorations) &&
        !isOnOrAdjacentToLandedPod

      // Summon movement: on mobile tap to move, on desktop right-click to move (left-click deselects)
      if (gameState.selectedSummonId) {
        const selectedSummon = gameState.summonedUnits.find((s) => s.id === gameState.selectedSummonId)
        // Wolf, fox, and boar can all be moved
        if (selectedSummon && (selectedSummon.type === "wolf" || selectedSummon.type === "fox" || selectedSummon.type === "boar")) {
          if (isDesktop) {
            // On desktop left-click, just deselect the summon (right-click to move)
            gameState.actions.deselectSummon()
            setSelectedTileMenu(null)
            setGhostTower(null)
            setLastClickedTile(null)
            return
          } else {
            // On mobile, tap to move the summon (like hero)
            gameState.actions.moveSummon(gameState.selectedSummonId, tileCenter)
            gameState.actions.deselectSummon()
            setSelectedTileMenu(null)
            setGhostTower(null)
            setLastClickedTile(null)
            return
          }
        }
      }

      // Hero selected: on desktop left-click deselects and optionally opens build menu, on mobile tap moves hero
      if (gameState.selectedHeroId && !isOnLandedPod) {
        if (isDesktop) {
          // On desktop left-click: deselect hero (right-click to move)
          gameState.actions.setSelectedHeroId(null)
          // If buildable tile, open build menu instead of just deselecting
          if (canBuild) {
            gameState.actions.setSelectedTowerId(null)
            const canvas = canvasContainerRef.current?.querySelector("canvas") as HTMLCanvasElement | null
            if (canvas) {
              const rect = canvas.getBoundingClientRect()
              const scaleX = rect.width / canvas.width
              const scaleY = rect.height / canvas.height
              const screenX = rect.left + (tileCenter.x - viewport.x) * viewport.zoom * scaleX
              const screenY = rect.top + (tileCenter.y - viewport.y) * viewport.zoom * scaleY

              setSelectedTileMenu({
                tilePosition: tileCenter,
                screenPosition: { x: screenX, y: screenY },
              })
              setLastClickedTile({ x: tile.tileX, y: tile.tileY })
            }
          } else {
            setSelectedTileMenu(null)
            setGhostTower(null)
            setLastClickedTile(null)
          }
          return
        } else {
          // On mobile, tap to move the hero
          gameState.actions.moveHero(tileCenter)
          gameState.actions.setSelectedHeroId(null)
          setSelectedTileMenu(null)
          setGhostTower(null)
          setLastClickedTile(null)
          return
        }
      }

      // Check if there's a dropped item on this tile (blocks build menu)
      const hasDroppedItemOnTile = gameState.droppedItems.some((item) => {
        const itemTile = worldToTile(item.position.x, item.position.y)
        return itemTile.tileX === tile.tileX && itemTile.tileY === tile.tileY
      })

      // Open build menu if buildable tile and no dropped item on it
      if (canBuild && !gameState.selectedHeroId && !hasDroppedItemOnTile) {
        const isSameTile = lastClickedTile && lastClickedTile.x === tile.tileX && lastClickedTile.y === tile.tileY

        if (isSameTile && selectedTileMenu) {
          setSelectedTileMenu(null)
          setGhostTower(null)
          setLastClickedTile(null)
        } else {
          gameState.actions.setSelectedTowerId(null)
          gameState.actions.setSelectedHeroId(null)

          const canvas = canvasContainerRef.current?.querySelector("canvas") as HTMLCanvasElement | null
          if (canvas) {
            const rect = canvas.getBoundingClientRect()
            const scaleX = rect.width / canvas.width
            const scaleY = rect.height / canvas.height
            const screenX = rect.left + (tileCenter.x - viewport.x) * viewport.zoom * scaleX
            const screenY = rect.top + (tileCenter.y - viewport.y) * viewport.zoom * scaleY

            setSelectedTileMenu({
              tilePosition: tileCenter,
              screenPosition: { x: screenX, y: screenY },
            })
            setLastClickedTile({ x: tile.tileX, y: tile.tileY })
          }
        }
      } else if (!canBuild && !isOnPath) {
        // Clicking on empty space closes all menus and deselects tower
        gameState.actions.setSelectedTowerId(null)
        gameState.actions.setSelectedHeroId(null)
        gameState.actions.deselectEnemy()
        gameState.actions.deselectSoldier()
        setSelectedTileMenu(null)
        setGhostTower(null)
        setLastClickedTile(null)
      }
    },
    [gameState, getCanvasPosition, selectedTileMenu, lastClickedTile, isProcessingPayment, viewport, selectedDroppedItem, selectedDroppodMenu],
  )

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      // Skip selection when space bar is pressed (panning mode)
      if (isSpacePressedRef.current) return
      handleTapAtPosition(e.clientX, e.clientY)
    },
    [handleTapAtPosition],
  )

  // Right-click handler for move/attack-move orders on desktop
  const handleCanvasRightClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault() // Prevent context menu

      if (gameState.gameState !== "playing") return
      // Block input during abduction animation
      if (gameState.abductionAnimation) return

      const worldPos = getCanvasPosition(e.clientX, e.clientY)
      const tileCenter = tileToWorldCenter(
        worldToTile(worldPos.x, worldPos.y).tileX,
        worldToTile(worldPos.x, worldPos.y).tileY,
      )

      // Check if a barracks tower is selected - right-click sets rally point
      const selectedTower = gameState.selectedTowerId
        ? gameState.towers.find((t) => t.id === gameState.selectedTowerId)
        : null
      if (selectedTower?.type === "barracks" && selectedTower.barracksState) {
        // Check if click is within rally radius
        if (isWithinRallyRadius(selectedTower.position, worldPos)) {
          const updatedTower = setRallyPoint(selectedTower, worldPos)
          gameState.setters.setTowers((prev) =>
            prev.map((t) => (t.id === selectedTower.id ? updatedTower : t))
          )
          // Force soldiers to stop attacking and move to new rally point for 2 seconds
          gameState.setters.setBarracksSoldiers((prev) =>
            forceSoldiersToRally(prev, selectedTower.id, 2000)
          )
          return
        }
      }

      // Check if right-clicked on a dropped item (quick pickup on desktop)
      for (const droppedItem of gameState.droppedItems) {
        if (pointInCircle(worldPos, droppedItem.position, 20)) {
          const heroPos = gameState.hero?.position
          if (heroPos) {
            const distToItem = Math.sqrt(
              (heroPos.x - droppedItem.position.x) ** 2 +
              (heroPos.y - droppedItem.position.y) ** 2
            )

            if (distToItem <= ITEM_PICKUP_RANGE) {
              // Within range - pick up directly without menu
              if (gameState.actions.canPickupItem(droppedItem.itemDefId)) {
                gameState.actions.pickupDroppedItem(droppedItem.id)
              }
              return
            } else {
              // Too far - move hero toward item
              gameState.actions.moveHero(droppedItem.position)
              return
            }
          }
        }
      }

      // Check if right-clicked on an enemy (attack-move: move to attack range)
      for (const enemy of gameState.enemies) {
        if (pointInCircle(worldPos, enemy.position, enemy.size)) {
          // Attack-move: move to max attack range from enemy
          if (gameState.selectedHeroId && gameState.hero && gameState.hero.position) {
            const hero = gameState.hero
            const dx = hero.position.x - enemy.position.x
            const dy = hero.position.y - enemy.position.y
            const dist = Math.sqrt(dx * dx + dy * dy)

            if (dist <= hero.attackRange) {
              // Already in range, no need to move
              return
            }

            // Move to a position at attack range from enemy (slightly inside range)
            // Use 90% of attack range (minimum 5px buffer) to ensure we're well within range
            const buffer = Math.max(5, hero.attackRange * 0.1)
            const attackPos = {
              x: enemy.position.x + (dx / dist) * (hero.attackRange - buffer),
              y: enemy.position.y + (dy / dist) * (hero.attackRange - buffer),
            }
            gameState.actions.moveHero(attackPos)
            return
          }
          if (gameState.selectedSummonId) {
            const selectedSummon = gameState.summonedUnits.find((s) => s.id === gameState.selectedSummonId)
            // Wolf, fox, and boar can all attack-move
            if (selectedSummon && (selectedSummon.type === "wolf" || selectedSummon.type === "fox" || selectedSummon.type === "boar")) {
              gameState.actions.moveSummon(gameState.selectedSummonId, enemy.position)
              return
            }
          }
          return
        }
      }

      // Move order to position (not on enemy)
      if (gameState.selectedSummonId) {
        const selectedSummon = gameState.summonedUnits.find((s) => s.id === gameState.selectedSummonId)
        // Wolf, fox, and boar can all be moved
        if (selectedSummon && (selectedSummon.type === "wolf" || selectedSummon.type === "fox" || selectedSummon.type === "boar")) {
          gameState.actions.moveSummon(gameState.selectedSummonId, tileCenter)
          return
        }
      }

      if (gameState.selectedHeroId && gameState.hero) {
        gameState.actions.moveHero(tileCenter)
        return
      }
    },
    [gameState, getCanvasPosition],
  )

  const handleCanvasTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // No preventDefault here: React registers touchstart passively, so it
      // would throw. Scroll suppression comes from touch-action: none CSS.

      if (e.touches.length === 2) {
        // Start pinch gesture
        const distance = getTouchDistance(e.touches)
        lastTouchDistanceRef.current = distance
        pinchStartZoomRef.current = viewport.zoom
        touchStartRef.current = getTouchCenter(e.touches)
        setIsAnimatingZoom(false)
        isPanningRef.current = false
        lastPanPositionRef.current = null
        hasPannedRef.current = true
      } else if (e.touches.length === 1) {
        isPanningRef.current = true
        lastPanPositionRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        }
        touchStartTimeRef.current = Date.now()
        touchStartPositionRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        }
        hasPannedRef.current = false
        setIsManualViewport(true)
      }
    },
    [viewport.zoom], // Removed getTouchDistance and getTouchCenter from dependencies as they are global
  )

  const handleCanvasTouchMove = useCallback(
    (e: React.TouchEvent) => {
      // No preventDefault here: React registers touchmove passively, so it
      // would throw. Scroll suppression comes from touch-action: none CSS.

      if (e.touches.length === 2 && lastTouchDistanceRef.current !== null) {
        isPanningRef.current = false
        lastPanPositionRef.current = null
        hasPannedRef.current = true

        // Close menus when pinch zooming
        setSelectedTileMenu(null)
        setSelectedDroppodMenu(null)

        const currentDistance = getTouchDistance(e.touches)
        const scale = currentDistance / lastTouchDistanceRef.current
        didPinchRef.current = true

        const center = getTouchCenter(e.touches)
        const canvas = canvasContainerRef.current?.querySelector("canvas") as HTMLCanvasElement | null
        if (canvas) {
          const rect = canvas.getBoundingClientRect()
          const scaleX = canvas.width / rect.width
          const scaleY = canvas.height / rect.height

          const viewportCenterX = (center.x - rect.left) * scaleX
          const viewportCenterY = (center.y - rect.top) * scaleY

          // Functional update - two touch events between renders must compose,
          // not overwrite each other with stale closure values
          setViewport((prev) => {
            let newZoom = pinchStartZoomRef.current * scale
            const minZoom = getMinZoom(prev.width)
            newZoom = Math.max(minZoom, Math.min(MAX_ZOOM, newZoom)) // Clamped to dynamic min zoom and max

            const worldX = viewportCenterX / prev.zoom + prev.x
            const worldY = viewportCenterY / prev.zoom + prev.y

            const newViewWidth = prev.width / newZoom
            const newViewHeight = prev.height / newZoom

            const newX = Math.max(0, Math.min(WORLD_WIDTH - newViewWidth, worldX - viewportCenterX / newZoom))
            const newY = Math.max(0, Math.min(WORLD_HEIGHT - newViewHeight, worldY - viewportCenterY / newZoom))

            pinchStartZoomRef.current = newZoom // continuous zooming baseline
            return { ...prev, x: newX, y: newY, zoom: newZoom }
          })
        }

        lastTouchDistanceRef.current = currentDistance
      } else if (e.touches.length === 1 && isPanningRef.current && lastPanPositionRef.current) {
        const currentX = e.touches[0].clientX
        const currentY = e.touches[0].clientY

        if (touchStartPositionRef.current) {
          const totalDeltaX = Math.abs(currentX - touchStartPositionRef.current.x)
          const totalDeltaY = Math.abs(currentY - touchStartPositionRef.current.y)
          if (totalDeltaX > 10 || totalDeltaY > 10) {
            hasPannedRef.current = true
          }
        }

        const deltaX = lastPanPositionRef.current.x - currentX
        const deltaY = lastPanPositionRef.current.y - currentY

        const canvas = canvasContainerRef.current?.querySelector("canvas") as HTMLCanvasElement | null
        if (canvas) {
          const rect = canvas.getBoundingClientRect()
          const scaleX = canvas.width / rect.width
          const scaleY = canvas.height / rect.height

          // Functional update from prev so fast successive touch events
          // compose their deltas instead of overwriting each other
          setViewport((prev) => {
            const worldDeltaX = (deltaX * scaleX) / prev.zoom
            const worldDeltaY = (deltaY * scaleY) / prev.zoom
            const viewWidth = prev.width / prev.zoom
            const viewHeight = prev.height / prev.zoom
            return {
              ...prev,
              x: Math.max(0, Math.min(WORLD_WIDTH - viewWidth, prev.x + worldDeltaX)),
              y: Math.max(0, Math.min(WORLD_HEIGHT - viewHeight, prev.y + worldDeltaY)),
            }
          })
        }

        lastPanPositionRef.current = { x: currentX, y: currentY }
      }
    },
    [], // reads viewport via functional updates and refs only
  )

  const handleCanvasTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault()

      // Handle tap after touch ends if it was a quick, non-panned touch
      if (e.touches.length === 0) {
        const touchDuration = Date.now() - touchStartTimeRef.current
        const isTap = touchDuration < 200 && !hasPannedRef.current

        if (isTap && touchStartPositionRef.current) {
          // Use the initial touch position for the tap
          handleTapAtPosition(touchStartPositionRef.current.x, touchStartPositionRef.current.y)
        }

        // Reset panning state
        isPanningRef.current = false
        lastPanPositionRef.current = null
        touchStartPositionRef.current = null
        hasPannedRef.current = false // Reset hasPannedRef

        // Reset manual viewport timeout if no touches are active
        if (manualViewportTimeoutRef.current) {
          clearTimeout(manualViewportTimeoutRef.current)
        }
        manualViewportTimeoutRef.current = setTimeout(() => {
          setIsManualViewport(false)
        }, 3000) // Keep manual mode for a bit after touch ends to allow user to adjust
      }

      // Handle pinch gesture ending
      if (e.touches.length < 2) {
        lastTouchDistanceRef.current = null
        touchStartRef.current = null // Clear touch start reference for pinch

        // A pinch is a deliberate camera placement - keep the zoom exactly
        // where the fingers left it. (This used to snap to default/min zoom,
        // which threw away the level the user had just pinched to.)
        if (didPinchRef.current) {
          didPinchRef.current = false
          setTargetZoom(viewportRef.current.zoom)
          setIsAnimatingZoom(false)
          setIsManualViewport(true)
        }
      }
    },
    [handleTapAtPosition], // zoom/width read from viewportRef at event time
  )

  // Desktop scroll wheel zoom - zoom centered on mouse cursor
  const handleCanvasWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!isDesktop) return
      e.preventDefault()

      // Close menus when zooming
      setSelectedTileMenu(null)
      setSelectedDroppodMenu(null)

      const canvas = canvasContainerRef.current?.querySelector("canvas") as HTMLCanvasElement | null
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height

      // Get mouse position relative to canvas
      const mouseX = (e.clientX - rect.left) * scaleX
      const mouseY = (e.clientY - rect.top) * scaleY

      // Scale the step by how much was actually scrolled. A fixed ±10% per
      // event made trackpads (many small deltas) rocket to the zoom limit while
      // a notched wheel crawled. deltaMode 1 is lines, 2 is pages.
      const lineHeight = 16
      const rawDelta =
        e.deltaMode === 1 ? e.deltaY * lineHeight : e.deltaMode === 2 ? e.deltaY * rect.height : e.deltaY
      // Cap a single event's contribution so a flick can't jump the whole range
      const steps = Math.max(-4, Math.min(4, rawDelta / 100))
      const zoomDelta = Math.pow(0.9, steps) // scroll up (negative delta) zooms in

      // A wheel zoom is a deliberate camera placement - hold it rather than
      // letting the auto-framing pull the view back.
      setIsManualViewport(true)
      setIsAnimatingZoom(false)

      // Functional update - reads fresh zoom/width so fast scroll events
      // compose and the min-zoom clamp uses the current viewport width
      setViewport((prev) => {
        const worldX = mouseX / prev.zoom + prev.x
        const worldY = mouseY / prev.zoom + prev.y

        const minZoom = getMinZoom(prev.width)
        const newZoom = Math.max(minZoom, Math.min(MAX_ZOOM, prev.zoom * zoomDelta))
        if (newZoom === prev.zoom) return prev

        const newViewWidth = prev.width / newZoom
        const newViewHeight = prev.height / newZoom

        // Keep the world point under the mouse cursor in the same screen position
        const newX = Math.max(0, Math.min(WORLD_WIDTH - newViewWidth, worldX - mouseX / newZoom))
        const newY = Math.max(0, Math.min(WORLD_HEIGHT - newViewHeight, worldY - mouseY / newZoom))

        setTargetZoom(newZoom)
        return { ...prev, x: newX, y: newY, zoom: newZoom }
      })
    },
    [isDesktop],
  )

  // Manually attach wheel event listener with passive: false to allow preventDefault.
  // gameState is in the deps because the canvas wrapper only exists once the game
  // is on screen - without it this effect ran while the ref was still null (title
  // screen) and never re-ran, so desktop wheel zoom silently did nothing.
  useEffect(() => {
    const container = canvasContainerRef.current
    if (!container || !isDesktop) return

    const wheelHandler = (e: WheelEvent) => {
      handleCanvasWheel(e as unknown as React.WheelEvent)
    }

    container.addEventListener("wheel", wheelHandler, { passive: false })

    return () => {
      container.removeEventListener("wheel", wheelHandler)
    }
  }, [handleCanvasWheel, isDesktop, gameState.gameState])

  // Space bar panning handlers (desktop only)
  const handleSpaceMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isSpacePressedRef.current) return
      spaceMouseStartRef.current = { x: e.clientX, y: e.clientY }
    },
    [],
  )

  const handleSpaceMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isSpacePressedRef.current || !spaceMouseStartRef.current) return

      const deltaX = spaceMouseStartRef.current.x - e.clientX
      const deltaY = spaceMouseStartRef.current.y - e.clientY

      const canvas = canvasContainerRef.current?.querySelector("canvas")
      if (canvas) {
        const rect = canvas.getBoundingClientRect()

        // Functional update from prev - fast mouse-move events must compose
        setViewport((prev) => {
          const scaleX = prev.width / rect.width
          const scaleY = prev.height / rect.height
          const worldDeltaX = (deltaX * scaleX) / prev.zoom
          const worldDeltaY = (deltaY * scaleY) / prev.zoom
          const viewWidth = prev.width / prev.zoom
          const viewHeight = prev.height / prev.zoom
          return {
            ...prev,
            x: Math.max(0, Math.min(WORLD_WIDTH - viewWidth, prev.x + worldDeltaX)),
            y: Math.max(0, Math.min(WORLD_HEIGHT - viewHeight, prev.y + worldDeltaY)),
          }
        })

        setIsManualViewport(true)
      }

      spaceMouseStartRef.current = { x: e.clientX, y: e.clientY }
    },
    [],
  )

  const handleSpaceMouseUp = useCallback(() => {
    spaceMouseStartRef.current = null
  }, [])


  // The inventory requires these callbacks, but nothing renders a drag
  // affordance from them - they used to drive a `draggingItemSlot` state that
  // was never read, i.e. a re-render per drag event for no visible change.
  const handleItemDragStart = useCallback(() => {}, [])
  const handleItemDragEnd = useCallback(() => {}, [])

  // Handle drop on canvas container
  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }, [])

  const handleCanvasDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      try {
        const data = JSON.parse(e.dataTransfer.getData("application/json"))
        if (typeof data.slotIndex === "number") {
          // Drop the item from inventory
          gameState.actions.dropItemFromInventory(data.slotIndex)
        }
      } catch {
        // Invalid drag data, ignore
      }
    },
    [gameState.actions],
  )

  const handleSelectTowerFromMenu = useCallback(
    async (towerType: TowerType) => {
      if (!selectedTileMenu) return

      const towerConfig = towerTypes[towerType]

      // Premium towers (no free token) cost crystals - spent server-side so
      // no wallet prompt interrupts combat
      if (towerConfig.isPremium && gameState.freePremiumTowers <= 0) {
        setIsProcessingPayment(true)
        const spend = await spendCrystals(towerConfig.premiumCostCrystals || 0, "spend:premium-tower")
        setIsProcessingPayment(false)

        if (!spend.ok) {
          setPaymentError(spend.error || "Not enough crystals")
          setTimeout(() => setPaymentError(null), 5000)
          setSelectedTileMenu(null)
          setGhostTower(null)
          return
        }
      }

      const success = gameState.actions.placeTower(towerType, selectedTileMenu.tilePosition)
      if (success) {
        setSelectedTileMenu(null)
        setGhostTower(null)
      }
    },
    [selectedTileMenu, gameState.actions, gameState.freePremiumTowers, spendCrystals],
  )

  const handleCloseMenu = useCallback(() => {
    setSelectedTileMenu(null)
    setGhostTower(null)
    setLastClickedTile(null)
  }, [])

  const handleCloseDroppodMenu = useCallback(() => {
    setSelectedDroppodMenu(null)
  }, [])

  // Calculate droppod clone cost - exponential scaling based on hero level
  // Base 50, multiplied by 1.5^level: 50, 75, 112, 168, 253, 379...
  const getDroppodCloneCost = useCallback((heroLevel: number): number => {
    return Math.floor(50 * Math.pow(1.5, heroLevel - 1))
  }, [])

  const droppodCloneCost = gameState.hero ? getDroppodCloneCost(gameState.hero.level) : 50

  // Handle clone from droppod menu (uses droppod pricing)
  const handleCloneHeroFromDroppod = useCallback(async () => {
    if (!gameState.hero || gameState.stats.money < droppodCloneCost) return

    // Deduct gold
    gameState.setters.setStats((prev) => ({ ...prev, money: prev.money - droppodCloneCost }))

    // Clone hero with current inventory
    await cloneHero(gameState.hero, gameState.heroInventory)

    // Increment beacon charges
    const updatedInventory = incrementBeaconCharges(gameState.heroInventory)
    gameState.setters.setHeroInventory(updatedInventory)

    // Get new charge count for toast
    const newCharges = updatedInventory.slots[0]?.charges || 0

    // Update clone count and trigger particle effects
    const allClones = loadClones()
    setCloneCount(allClones.length)
    setShowCloneParticles(true)
    setShowBeaconParticles(true)

    // Build concise toast about snapshot
    const heroLevel = gameState.hero.level
    const toastMessage = `Snapshot saved (Lv ${heroLevel}) • ${newCharges} charge${newCharges !== 1 ? 's' : ''}`

    // Show toast notification
    setCloneToast(toastMessage)

    // Reset particle trigger after animation
    setTimeout(() => {
      setShowCloneParticles(false)
    }, 100)

    // Reset beacon particles after animation
    setTimeout(() => {
      setShowBeaconParticles(false)
    }, 100)

    // Hide toast after 4 seconds (longer for more info)
    setTimeout(() => {
      setCloneToast(null)
    }, 4000)
  }, [gameState.hero, gameState.heroInventory, gameState.stats.money, gameState.setters, droppodCloneCost])

  // Handle clone from shop (uses shop pricing with cost passed in)
  const handleCloneHeroFromShop = useCallback(async (cost: number) => {
    if (!gameState.hero || gameState.stats.money < cost) return

    // Deduct gold using functional update to ensure latest state
    gameState.setters.setStats((prev) => ({ ...prev, money: prev.money - cost }))

    // Clone hero with current inventory
    await cloneHero(gameState.hero, gameState.heroInventory)

    // Increment beacon charges using functional update to ensure latest state
    let newCharges = 0
    gameState.setters.setHeroInventory((prev) => {
      const updated = incrementBeaconCharges(prev)
      newCharges = updated.slots[0]?.charges || 0
      return updated
    })

    // Update clone count and trigger particle effects
    const allClones = loadClones()
    setCloneCount(allClones.length)
    setShowCloneParticles(true)
    setShowBeaconParticles(true)

    // Build concise toast about snapshot
    const heroLevel = gameState.hero.level
    const toastMessage = `Snapshot saved (Lv ${heroLevel}) • ${newCharges} charge${newCharges !== 1 ? 's' : ''}`

    // Show toast notification
    setCloneToast(toastMessage)

    // Reset particle trigger after animation
    setTimeout(() => {
      setShowCloneParticles(false)
    }, 100)

    // Reset beacon particles after animation
    setTimeout(() => {
      setShowBeaconParticles(false)
    }, 100)

    // Hide toast after 4 seconds (longer for more info)
    setTimeout(() => {
      setCloneToast(null)
    }, 4000)
  }, [gameState.hero, gameState.heroInventory, gameState.stats.money, gameState.setters])

  const handleOpenPhaseTerminal = useCallback(() => {
    setIsPhaseTerminalOpen(true)
    setSelectedDroppodMenu(null)
  }, [])

  const handleClosePhaseTerminal = useCallback(() => {
    setIsPhaseTerminalOpen(false)
  }, [])

  const handleCloseStarMapShop = useCallback(() => {
    setIsStarMapShopOpen(false)
    // Just close the shop without completing the node - user can reopen
    // The node will be completed when user clicks Travel on the map
  }, [])

  const handleOpenUpgradeTree = useCallback(() => {
    setIsUpgradeTreeOpen(true)
    setSelectedDroppodMenu(null)
  }, [])

  const handleCloseUpgradeTree = useCallback(() => {
    setIsUpgradeTreeOpen(false)
  }, [])

  const handleUnlockUpgrade = useCallback(
    (upgradeId: string, cost: number) => {
      if (gameState.stats.money < cost) return

      gameState.setters.setStats((prev) => ({
        ...prev,
        money: prev.money - cost,
      }))

      gameState.actions.unlockUpgrade(upgradeId)
    },
    [gameState.stats.money, gameState.setters, gameState.actions]
  )

  // Handle spaceship arrival at node (updates current node before level starts)
  const handleMapNodeArrival = useCallback((node: MapNode) => {
    gameState.actions.advanceMapNode(node)
  }, [gameState.actions])

  // Handle map node selection (starts the level)
  const handleMapNodeSelect = useCallback((node: MapNode) => {
    gameState.actions.selectMapNode(node)

    // If it's an event node (shop), open the star map shop
    if (node.type === "event") {
      setIsStarMapShopOpen(true)
    }
  }, [gameState.actions])

  const handleBuyItemFromTerminal = useCallback(
    (itemDefId: string, price: number) => {
      gameState.actions.buyItem(itemDefId, price)
    },
    [gameState.actions],
  )

  const handleRestartWithCurrentHero = useCallback(() => {
    gameState.actions.restartWithCurrentHero()
    setSelectedDroppodMenu(null)
  }, [gameState.actions])

  const selectedTower = gameState.towers.find((t) => t.id === gameState.selectedTowerId)

  const isZoomedOut = viewport.zoom < ZOOM_DEFAULT * 0.9

  // Get pending skill info for targeting preview
  const pendingSkillInfo = (() => {
    if (!gameState.pendingSkillId || !gameState.hero) return null
    const skill = gameState.hero.skills.find((s) => s.id === gameState.pendingSkillId)
    if (!skill) return null
    const levelStats = skill.levels[skill.currentLevel - 1]
    return {
      type: skill.type,
      summonType: levelStats?.summonType,
      range: levelStats?.range,
    }
  })()

  // Get pending item info for targeting preview
  const pendingItemInfo = (() => {
    if (gameState.pendingItemSlot === null) return null
    const slot = gameState.heroInventory.slots[gameState.pendingItemSlot]
    if (!slot) return null
    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef) return null

    // Find targeting effect (teleport or lure)
    const teleportEffect = itemDef.effects.find((e) => e.type === "teleport")
    if (teleportEffect) {
      return { effectType: "teleport" as const }
    }
    const lureEffect = itemDef.effects.find((e) => e.type === "lure")
    if (lureEffect) {
      return { effectType: "lure" as const, radius: lureEffect.value }
    }
    return null
  })()

  // Removed getPillarboxStyle, logic moved to PillarboxLayout
  const renderGameContent = () => {
    return (
      <>
        {gameState.gameState === "title" && (
          <TitleScreen
            onStartGame={handleStartGame}
            pendingRun={pendingRun}
            onResumeRun={handleResumeRun}
            onDiscardRun={handleDiscardRun}
          />
        )}

        {gameState.gameState === "heroSelect" && (
          <HeroSelectScreen
            onSelectHero={(heroType) => {
              gameState.actions.selectHero(heroType)
            }}
            selectedHero={gameState.selectedHeroType}
          />
        )}

        {gameState.gameState === "dropping" && (
          <DropPodMinigame
            onComplete={gameState.actions.onDropPodComplete}
            selectedHero={gameState.selectedHeroType || "knight"}
          />
        )}

        {gameState.gameState === "landing" && gameState.level && (
          <>
            <LandingSequence
              level={gameState.level}
              onComplete={(landingPosition) => {
                setLandingSequenceCompleted(true)
                gameState.actions.onLandingComplete(landingPosition)

                // Show tutorial on first level if user hasn't seen it
                if (gameState.currentLevelIndex === 0 && !hasSeenTutorial) {
                  openTutorial()
                }
              }}
            />
            {/* Show loading overlay ONLY if landing sequence finished but Phase 3 is still loading */}
            {landingSequenceCompleted && !isPhaseComplete(3) && (
              <LoadingOverlay
                progress={assetState.phase3.progress}
                isComplete={isPhaseComplete(3)}
                minDelayMs={0}
              />
            )}
          </>
        )}

        {(gameState.gameState === "playing" ||
          gameState.gameState === "paused" ||
          gameState.gameState === "defeat") && (
          <>
            {/* Wrapper for game content that turns grayscale on defeat */}
            {/* On desktop landscape, break out of pillarbox for full-width level rendering */}
            <div
              className={cn(
                "flex-1 flex flex-col relative",
                isDesktop && isPillarboxed && "fixed inset-0 z-20"
              )}
              style={{
                filter: gameState.gameState === "defeat" ? "grayscale(100%)" : "none",
              }}
            >
              <GameHUD
                stats={gameState.stats}
                level={gameState.level}
                interestAnimation={gameState.interestAnimation}
                sellAnimation={gameState.sellAnimation}
                interestBonusPercent={gameState.hero ? getInterestBonus(gameState.hero) : 0}
                canvasLeftOffset={canvasRightOffset}
                gameSpeed={gameSpeed}
                onToggleGameSpeed={toggleGameSpeed}
                onPause={gameState.gameState === "playing" ? gameState.actions.pauseGame : undefined}
              />
              <div
                ref={canvasContainerRef}
                id="game-canvas-wrapper"
                className="flex-1 relative overflow-hidden touch-none"
                onTouchStart={handleCanvasTouchStart}
                onTouchMove={handleCanvasTouchMove}
                onTouchEnd={handleCanvasTouchEnd}
                onMouseDown={handleSpaceMouseDown}
                onMouseMove={handleSpaceMouseMove}
                onMouseUp={handleSpaceMouseUp}
                onMouseLeave={handleSpaceMouseUp}
                onDragOver={handleCanvasDragOver}
                onDrop={handleCanvasDrop}
              >
                {gameState.level && (
                  <GameCanvas
                    level={gameState.level}
                    towers={gameState.towers}
                    enemies={gameState.enemies}
                    projectiles={gameState.projectiles}
                    skillEffects={gameState.skillEffects}
                    summonedUnits={gameState.summonedUnits}
                    rootsEffects={gameState.rootsEffects}
                    fireEffects={gameState.fireEffects}
                    laserBeamEffects={gameState.laserBeamEffects}
                    barracksSoldiers={gameState.barracksSoldiers}
                    corpses={gameState.corpses}
                    particleEffects={gameState.particleEffects}
                    hero={gameState.hero}
                    landedPod={gameState.landedPod}
                    onClick={handleCanvasClick}
                    onRightClick={isDesktop ? handleCanvasRightClick : undefined}
                    selectedTowerId={gameState.selectedTowerId}
                    selectedShrineMenuId={selectedShrineMenu?.shrineId || null}
                    selectedHeroId={gameState.selectedHeroId}
                    selectedSummonId={gameState.selectedSummonId}
                    selectedSoldierId={gameState.selectedSoldierId}
                    selectedEnemyId={gameState.selectedEnemyId}
                    viewport={viewport}
                    isMenuOpen={!!selectedTileMenu}
                    onParticleComplete={gameState.actions.removeParticleEffect}
                    ghostTower={ghostTower}
                    selectedTilePosition={selectedTileMenu?.tilePosition}
                    isTargetingSkill={!!gameState.pendingSkillId}
                    pendingSkillInfo={pendingSkillInfo}
                    mobileTargetPreviewPos={mobileTargetPreviewPos}
                    isTargetingItem={gameState.pendingItemSlot !== null}
                    pendingItemInfo={pendingItemInfo}
                    aoeVisualEffects={gameState.aoeVisualEffects}
                    activeItemEffects={gameState.activeItemEffects}
                    droppedItems={gameState.droppedItems}
                    isDroppodSelected={!!selectedDroppodMenu}
                    screenShakeOffset={gameState.screenShakeOffset}
                    cloneCount={cloneCount}
                    showDroppodTutorial={showDroppodTutorial && gameState.currentLevelIndex === 0}
                    upgradeModifiers={getUpgradeModifiers(gameState.upgradeState)}
                    upgradeState={gameState.upgradeState}
                    abductionAnimation={gameState.abductionAnimation}
                    soldierSpawnPortraits={gameState.soldierSpawnPortraits}
                    itemDropMessages={gameState.itemDropMessages}
                  />
                )}
                {selectedTileMenu && (
                  <RadialTowerMenu
                    position={selectedTileMenu.screenPosition}
                    money={gameState.stats.money}
                    onSelectTower={handleSelectTowerFromMenu}
                    onClose={handleCloseMenu}
                    onProcessingPayment={setIsProcessingPayment}
                    freePremiumTowers={gameState.freePremiumTowers}
                    isMobile={!isDesktop}
                  />
                )}
                {selectedDroppedItem && (() => {
                  const droppedItem = gameState.droppedItems.find((item) => item.id === selectedDroppedItem.itemId)
                  if (!droppedItem) return null
                  return (
                    <DroppedItemMenu
                      droppedItem={droppedItem}
                      screenPosition={selectedDroppedItem.screenPosition}
                      canPickup={gameState.actions.canPickupItem(droppedItem.itemDefId)}
                      onPickup={() => gameState.actions.pickupDroppedItem(droppedItem.id)}
                      onDestroy={() => gameState.actions.destroyDroppedItem(droppedItem.id)}
                      onClose={() => setSelectedDroppedItem(null)}
                    />
                  )
                })()}
                {selectedDroppodMenu && (
                  <RadialDroppodMenu
                    position={selectedDroppodMenu.screenPosition}
                    money={gameState.stats.money}
                    cloneCost={droppodCloneCost}
                    onCloneHero={handleCloneHeroFromDroppod}
                    onOpenPhaseTerminal={handleOpenPhaseTerminal}
                    onOpenUpgradeTree={handleOpenUpgradeTree}
                    onRestart={handleRestartWithCurrentHero}
                    onClose={handleCloseDroppodMenu}
                    isMobile={!isDesktop}
                  />
                )}
                {selectedShrineMenu && gameState.hero && (
                  <RadialShrineMenu
                    position={selectedShrineMenu.screenPosition}
                    money={gameState.stats.money}
                    heroSkills={gameState.hero.skills}
                    heroismLevel={gameState.heroismLevel}
                    shrineLevel={selectedShrineMenu.shrineLevel}
                    onUpgradeHeroism={() => {
                      const cost = getHeroismCost(gameState.heroismLevel)
                      if (gameState.stats.money >= cost && gameState.heroismLevel < 7) {
                        gameState.actions.purchaseHeroism(cost)
                      }
                    }}
                    onUpgradeHeroSkill={(skillId) => {
                      gameState.actions.purchaseSkillUpgrade(skillId)
                    }}
                    onClose={() => setSelectedShrineMenu(null)}
                    isMobile={!isDesktop}
                  />
                )}
                <PhaseTerminalWindow
                  isOpen={isPhaseTerminalOpen}
                  money={gameState.stats.money}
                  heroLevel={gameState.hero?.level ?? 1}
                  inventory={gameState.heroInventory}
                  onClose={handleClosePhaseTerminal}
                  onBuyItem={handleBuyItemFromTerminal}
                  onBuyCloneCharge={handleCloneHeroFromShop}
                />
                {selectedTower && (
                  <TowerInfoPanel
                    tower={selectedTower}
                    towerScreenPosition={
                      isDesktop
                        ? {
                            x: (selectedTower.position.x - viewport.x) * viewport.zoom,
                            y: (selectedTower.position.y - viewport.y) * viewport.zoom,
                          }
                        : undefined
                    }
                    money={gameState.stats.money}
                    upgradeModifiers={getUpgradeModifiers(gameState.upgradeState)}
                    isLastWaveStarted={
                      gameState.level
                        ? gameState.currentWaveIndex >= gameState.level.waves.length - 1 && !gameState.showNextWaveButton
                        : false
                    }
                    onUpgrade={() => gameState.actions.upgradeTower(selectedTower.id)}
                    onSell={() => gameState.actions.sellTower(selectedTower.id)}
                    onClose={() => gameState.actions.setSelectedTowerId(null)}
                  />
                )}
                {gameState.level && (gameState.showNextWaveButton || gameState.waveStartTime) && (
                  <NextWaveButton
                    level={gameState.level}
                    viewport={viewport}
                    onStartWave={handleStartWave}
                    onCallEarly={gameState.actions.callWaveEarly}
                    show={gameState.showNextWaveButton}
                    waveStartTime={gameState.waveStartTime}
                    isLastWave={gameState.currentWaveIndex >= gameState.level.waves.length - 1}
                    isFirstWave={gameState.currentWaveIndex === 0}
                    isMobile={isMobile}
                    isDesktop={isDesktop}
                    pillarWidth={pillarWidth}
                    showTutorial={showWaveTutorial && gameState.currentLevelIndex === 0 && gameState.currentWaveIndex === 0}
                    onTutorialDismiss={() => setShowWaveTutorial(false)}
                    currentWaveIndex={gameState.currentWaveIndex}
                  />
                )}
                {/* Visit shop button - shown when level is complete, before abduction */}
                {gameState.isLastWaveComplete && !gameState.abductionAnimation && (
                  <div className="absolute inset-x-0 z-50 flex justify-center" style={{ bottom: "170px" }}>
                    <NineSliceButton
                      id="visit-shop-button"
                      onClick={(e) => {
                        e.stopPropagation()
                        gameState.actions.startAbductionAnimation()
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        gameState.actions.startAbductionAnimation()
                      }}
                      className="text-base"
                    >
                      <span className="text-black">VISIT SHOP</span>
                    </NineSliceButton>
                  </div>
                )}
                {/* Abduction score overlay - shown during score_count and score_display phases */}
                {(gameState.abductionAnimation?.phase === "score_count" || gameState.abductionAnimation?.phase === "score_display") && (
                  <AbductionScoreOverlay
                    startScore={gameState.abductionAnimation.scoreAtLevelStart}
                    enemyScore={gameState.abductionAnimation.enemyScore}
                    towerScore={gameState.abductionAnimation.towerScore}
                    progress={gameState.abductionAnimation.phase === "score_display" ? 1 : Math.min((gameNow() - gameState.abductionAnimation.phaseStartTime) / 2000, 1)}
                  />
                )}
              </div>
              <GameBottomBar
                enemies={gameState.enemies}
                towers={gameState.towers}
                hero={gameState.hero}
                summonedUnits={gameState.summonedUnits}
                level={gameState.level}
                viewport={viewport}
                isHeroSelected={!!gameState.selectedHeroId}
                selectedEnemyId={gameState.selectedEnemyId}
                selectedSummonId={gameState.selectedSummonId}
                selectedSoldierId={gameState.selectedSoldierId}
                selectedTowerId={gameState.selectedTowerId}
                barracksSoldiers={gameState.barracksSoldiers}
                isDroppodSelected={!!selectedDroppodMenu}
                cloneCount={cloneCount}
                showCloneParticles={showCloneParticles}
                onViewportChange={handleViewportChange}
                onSelectHero={handleSelectHeroDesktop}
                onDeselectHero={() => gameState.actions.setSelectedHeroId(null)}
                onDeselectEnemy={gameState.actions.deselectEnemy}
                onDeselectSummon={gameState.actions.deselectSummon}
                onDeselectSoldier={gameState.actions.deselectSoldier}
                onDeselectTower={() => gameState.actions.setSelectedTowerId(null)}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                isZoomedOut={isZoomedOut}
                stats={gameState.stats}
                heroTypes={heroTypes}
                selectedHeroType={gameState.selectedHeroType}
                freePremiumTowers={gameState.freePremiumTowers}
                onUseSkill={gameState.actions.useSkill}
                onUpgradeSkill={gameState.actions.upgradeHeroSkill}
                onMobileSkillActivated={() => {
                  // Set mobile target preview to enable immediate execution on next canvas click
                  // Use a sentinel value to indicate that the first tap has been done
                  if (gameState.hero) {
                    setMobileTargetPreviewPos(gameState.hero.position)
                  }
                }}
                isDesktop={isDesktop}
                pendingSkillId={gameState.pendingSkillId}
                heroInventory={gameState.heroInventory}
                onUseItem={gameState.actions.useItem}
                onDropItem={gameState.actions.dropItemFromInventory}
                pendingItemSlot={gameState.pendingItemSlot}
                lastUpgradedSkillId={gameState.lastUpgradedSkillId}
                onItemDragStart={handleItemDragStart}
                onItemDragEnd={handleItemDragEnd}
                pillarboxedWidth={Math.floor(gameHeight * (9 / 16))}
                showBeaconParticles={showBeaconParticles}
                cloneToast={paymentError ?? cloneToast}
                toastTone={paymentError ? "error" : "info"}
              />

              <SkillUpgradeButton
                hero={gameState.hero}
                onUpgrade={gameState.actions.upgradeHeroSkill}
                isDesktop={isDesktop}
                pillarWidth={pillarWidth}
                showTutorial={showDroppodTutorial && gameState.currentLevelIndex === 0}
              />

              {/* Tour system - only show during active gameplay on first level */}
              {gameState.gameState === "playing" && gameState.currentLevelIndex === 0 && (
                <TourOverlay
                  isMobile={isMobile}
                  heroScreenRect={(() => {
                    // Calculate hero screen rect for highlighting
                    if (!gameState.hero || !canvasContainerRef.current) return null
                    const canvas = canvasContainerRef.current.querySelector("canvas") as HTMLCanvasElement | null
                    if (!canvas) return null
                    const rect = canvas.getBoundingClientRect()
                    const scaleX = rect.width / canvas.width
                    const scaleY = rect.height / canvas.height
                    const heroSize = 64 // Approximate hero sprite size
                    const screenX = rect.left + (gameState.hero.position.x - viewport.x) * viewport.zoom * scaleX
                    const screenY = rect.top + (gameState.hero.position.y - viewport.y) * viewport.zoom * scaleY
                    return new DOMRect(
                      screenX - (heroSize / 2) * viewport.zoom * scaleX,
                      screenY - (heroSize / 2) * viewport.zoom * scaleY,
                      heroSize * viewport.zoom * scaleX,
                      heroSize * viewport.zoom * scaleY
                    )
                  })()}
                  onSelectHero={() => {
                    // Select the hero for the hero movement step
                    if (gameState.hero) {
                      gameState.actions.setSelectedHeroId(gameState.hero.id)
                    }
                  }}
                  onOpenTowerMenu={() => {
                    // Find a buildable tile near the center and open the tower menu
                    if (!gameState.level || !canvasContainerRef.current) return
                    const canvas = canvasContainerRef.current.querySelector("canvas") as HTMLCanvasElement | null
                    if (!canvas) return

                    // Find the first buildable tile (scan from center outward)
                    const centerTileX = Math.floor(WORLD_WIDTH / TILE_SIZE / 2)
                    const centerTileY = Math.floor(WORLD_HEIGHT / TILE_SIZE / 2)

                    for (let radius = 0; radius < 10; radius++) {
                      for (let dx = -radius; dx <= radius; dx++) {
                        for (let dy = -radius; dy <= radius; dy++) {
                          if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue // Only check perimeter
                          const tileX = centerTileX + dx
                          const tileY = centerTileY + dy
                          const tileCenter = tileToWorldCenter(tileX, tileY)

                          // Check if this tile is buildable
                          const isOnOrAdjacentToLandedPod = gameState.landedPod && (
                            (tileX === gameState.landedPod.tileX && tileY === gameState.landedPod.tileY) ||
                            (tileX === gameState.landedPod.tileX && tileY === gameState.landedPod.tileY - 1) ||
                            (tileX === gameState.landedPod.tileX && tileY === gameState.landedPod.tileY + 1) ||
                            (tileX === gameState.landedPod.tileX - 1 && tileY === gameState.landedPod.tileY) ||
                            (tileX === gameState.landedPod.tileX + 1 && tileY === gameState.landedPod.tileY)
                          )

                          if (canPlaceTower(tileCenter, "archer", gameState.towers, gameState.level.path, gameState.level.pathWidth, gameState.level.tilemap?.objectDecorations, gameState.level.tilemap?.decorations) && !isOnOrAdjacentToLandedPod) {
                            // Found a buildable tile, open the menu
                            const rect = canvas.getBoundingClientRect()
                            const scaleX = rect.width / canvas.width
                            const scaleY = rect.height / canvas.height
                            const screenX = rect.left + (tileCenter.x - viewport.x) * viewport.zoom * scaleX
                            const screenY = rect.top + (tileCenter.y - viewport.y) * viewport.zoom * scaleY

                            setSelectedTileMenu({
                              tilePosition: tileCenter,
                              screenPosition: { x: screenX, y: screenY },
                            })
                            setLastClickedTile({ x: tileX, y: tileY })
                            return
                          }
                        }
                      }
                    }
                  }}
                  onSelectTower={() => {
                    // Select the first tower if one exists
                    if (gameState.towers.length > 0) {
                      gameState.actions.setSelectedTowerId(gameState.towers[0].id)
                    }
                  }}
                  onOpenDroppodMenu={() => {
                    // Open the droppod menu
                    if (!gameState.landedPod || !gameState.landedPod.position || !canvasContainerRef.current) return
                    const canvas = canvasContainerRef.current.querySelector("canvas") as HTMLCanvasElement | null
                    if (!canvas) return

                    const rect = canvas.getBoundingClientRect()
                    const scaleX = rect.width / canvas.width
                    const scaleY = rect.height / canvas.height
                    const screenX = rect.left + (gameState.landedPod.position.x - viewport.x) * viewport.zoom * scaleX
                    const screenY = rect.top + (gameState.landedPod.position.y - viewport.y) * viewport.zoom * scaleY

                    setSelectedDroppodMenu({
                      screenPosition: { x: screenX, y: screenY },
                    })
                  }}
                  onCleanupStep={() => {
                    // Clean up any menus/selections when moving to next step
                    setSelectedTileMenu(null)
                    setSelectedDroppodMenu(null)
                    gameState.actions.setSelectedTowerId(null)
                    gameState.actions.setSelectedHeroId(null)
                  }}
                />
              )}
            </div>

            {gameState.gameState === "paused" && !isCardOpen && (
              <PauseMenu onResume={gameState.actions.resumeGame} onRestart={gameState.actions.restartGame} />
            )}
            {gameState.isMapOpen && gameState.mapState && (
              <>
                <SpaceMapView
                  mapState={gameState.mapState}
                  onNodeSelect={handleMapNodeSelect}
                  onNodeArrival={handleMapNodeArrival}
                  onRevealEvent={gameState.actions.revealEventNode}
                  onCollectPelf={gameState.actions.collectPelfReward}
                  onCompleteShopNode={gameState.actions.completeMapNode}
                  onClose={gameState.actions.closeMap}
                  onOpenUpgradeTree={handleOpenUpgradeTree}
                />
                <StarMapShopWindow
                  isOpen={isStarMapShopOpen}
                  money={gameState.stats.money}
                  inventory={gameState.heroInventory}
                  heroLevel={gameState.hero?.level || 1}
                  onClose={handleCloseStarMapShop}
                  onBuyItem={(itemDefId, price) => gameState.actions.buyItem(itemDefId, price)}
                  onBuyCloneCharge={handleCloneHeroFromShop}
                />
                {/* Upgrade Tree as overlay on star map */}
                {isUpgradeTreeOpen && (
                  <UpgradeTreeWindow
                    money={gameState.stats.money}
                    upgradeState={gameState.upgradeState}
                    onUnlock={handleUnlockUpgrade}
                    onClose={handleCloseUpgradeTree}
                    isMobile={isMobile}
                  />
                )}
              </>
            )}
            {/* Upgrade Tree when map is closed (droppod access) */}
            {!gameState.isMapOpen && isUpgradeTreeOpen && (
              <UpgradeTreeWindow
                money={gameState.stats.money}
                upgradeState={gameState.upgradeState}
                onUnlock={handleUnlockUpgrade}
                onClose={handleCloseUpgradeTree}
                isMobile={isMobile}
              />
            )}
            {gameState.gameState === "defeat" && (
              <GameOverScreen
                level={gameState.currentLevelIndex + 1}
                score={gameState.stats.score}
                onRestart={gameState.actions.restartGame}
              />
            )}
          </>
        )}

        {gameState.gameState === "victory" && (
          <LevelCompleteScreen
            wave={gameState.stats.wave}
            score={gameState.stats.score}
            currentWaveIndex={gameState.currentWaveIndex}
            totalWaves={gameState.level?.waves.length || 0}
            currentLevelIndex={gameState.currentLevelIndex}
            totalLevels={gameState.totalLevels}
            onRestart={gameState.actions.restartGame}
            onNextLevel={gameState.actions.startNextLevel}
            onOpenMap={gameState.actions.openMapAfterLevel}
            isMobile={isMobile}
            isDesktop={isDesktop}
            heroTypes={heroTypes}
            selectedHeroType={gameState.selectedHeroType}
            hero={gameState.hero}
            heroInventory={gameState.heroInventory}
            stats={gameState.stats}
            onBuyItem={(itemDefId, price) => gameState.actions.buyItem(itemDefId, price)}
            onBuyCloneCharge={handleCloneHeroFromShop}
            onSpendGold={(amount) => gameState.actions.spendGold(amount)}
            onSellItem={(slotIndex, sellPrice) => gameState.actions.sellItem(slotIndex, sellPrice)}
          />
        )}
      </>
    )
  }

  if (isLandscape && isMobile) {
    return (
      <div id="landscape-warning" className="fixed inset-0 bg-background/95 flex items-center justify-center z-50">
        <div className="text-center p-8">
          <RedoIcon className="text-foreground mx-auto mb-6 animate-pulse" size={96} />
          <h2 className="text-3xl font-bold text-foreground mb-4">Rotate to Portrait Mode</h2>
          <p className="text-xl text-muted-foreground">
            Please rotate your device to portrait orientation to continue playing
          </p>
        </div>
      </div>
    )
  }

  return (
    <PillarboxLayout>
      <div
        id="game-root"
        className="w-full h-full flex flex-col bg-game-surface overflow-hidden select-none"
        style={{
          touchAction: "none",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
        }}
      >
        {renderGameContent()}
      </div>

      {/* Tutorial modal */}
      <TutorialModal isOpen={isTutorialOpen} onClose={closeTutorial} />

      {/* New enemy notification */}
      {currentNotification && gameState.gameState === "playing" && (
        <NewEnemyButton
          enemyId={currentNotification.enemyId}
          variant={currentNotification.variant}
          onClick={openCard}
          canvasSideOffset={canvasRightOffset}
        />
      )}

      {/* New enemy card modal */}
      {currentNotification && (
        <NewEnemyCard
          enemyId={currentNotification.enemyId}
          variant={currentNotification.variant}
          isOpen={isCardOpen}
          onClose={closeCard}
        />
      )}
    </PillarboxLayout>
  )
}

const getTouchDistance = (touches: React.TouchList): number => {
  if (touches.length < 2) return 0
  const dx = touches[0].clientX - touches[1].clientX
  const dy = touches[0].clientY - touches[1].clientY
  return Math.sqrt(dx * dx + dy * dy)
}

const getTouchCenter = (touches: React.TouchList): { x: number; y: number } => {
  if (touches.length < 2) {
    return { x: touches[0].clientX, y: touches[0].clientY }
  }
  return {
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
  }
}
